import {
  COMPASS_NAMES,
  STATUS_MESSAGE_RULES,
  announcementFor,
  displayColor,
  isAcknowledged,
  maySendStatusMessage,
  presentWhenSent,
  LAYOUT,
  MAP_FOLLOW,
  progressAlongRoute,
  receivesWarning,
  shortDistanceM,
  type Maneuver,
  type NavigationRoute,
  type RouteProgress,
  type Participant,
  type ParticipantRole,
  withOwnMeasurement,
  type PositionSample,
  type StatusMessageKind,
  type VehicleState,
  WAYPOINT_KIND_NAMES,
  anwesenheit,
  treffpunktHier,
  type Anwesenheit,
  type ArrivalEstimate,
  type Waypoint,
} from '@konvoi/shared';
import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';

import { navigate } from '../App.js';
import { api, ApiError } from '../lib/api.js';
import {
  CONVOY_LABELS,
  NAVIGATION_END_LABELS,
  NAVIGATION_END_SELF_CLEARING,
  STATUS_LABELS,
  berechneFahrtwerte,
  fahrzeugText,
  formatAge,
  formatDuration,
  formatMinutesSeconds,
  istVeraltet,
  navigationsAnlass,
  formatDelay,
  formatDistance,
  initials,
} from '../lib/labels.js';
import {
  leisteOffen as leseLeisteOffen,
  pruefmodusAn,
  setLeisteOffen as merkeLeisteOffen,
} from '../lib/anzeige.js';
import { Ansage } from '../lib/ansage.js';
import { ZeichenAnsage, ZeichenManöver } from '../lib/manoeverzeichen.js';
import { MeldungsZeichen } from '../lib/meldezeichen.js';
import {
  ZeichenGelesen,
  ZeichenOffen,
  ZeichenUnsichtbar,
  ZustandsZeichen,
} from '../lib/zustandszeichen.js';
import { useSelbstschluss } from '../lib/selbstschluss.js';
import { kartenführung as kartenführungFür } from '../lib/kartenfuehrung.js';
import { forgetSession, loadSession } from '../lib/session.js';
import { Einladung, beitrittsUrl } from '../lib/einladung.js';
import { drive } from '../lib/store.js';
import { currentTheme } from '../lib/theme.js';
import { ConvoyMap, type FollowMode, type MapOrientation } from '../map/ConvoyMap.js';

/**
 * Die Beschriftung, mit der eine Navigation beginnt.
 *
 * Zweimal auf dem Bildschirm — als große Fläche und im Hinweis der Führung —
 * und deshalb einmal hier. Das lange Wort trägt ein weiches Trennzeichen
 * (\u00AD): unsichtbar, solange die Fläche breit genug ist, und auf dem
 * Telefon bricht es an genau dieser Stelle um statt an einer, die der Browser
 * wählt.
 */
const NAVIGATION_STARTEN = 'Navigation zum Führungs\u00ADfahrzeug';

/**
 * Dieselbe Handlung, kürzer — für das Blatt, das die Führung auslöst.
 *
 * Dort steht die Überschrift „Das Führungsfahrzeug ruft dich zu sich" schon
 * darüber. Den Satz darunter noch einmal auszuschreiben, füllt nur die Fläche:
 * Auf drei Zeilen umbrochen wirkt eine Schaltfläche schwer zu treffen, obwohl
 * sie es nicht ist.
 */
const NAVIGATION_STARTEN_KURZ = 'Navigation starten';

/**
 * Die Ansicht während der Fahrt.
 *
 * Sie ist für einen Blick von einer Sekunde gebaut: Vollbildkarte, darunter der
 * Rückstand und zwei große Flächen. Wer führt, sieht zusätzlich die ganze
 * Kolonne eingepasst und die Liste nach Rückstand sortiert.
 */
export function DrivePage({ tourId }: { tourId: string }): React.JSX.Element {
  const state = useSyncExternalStore(drive.subscribe, drive.snapshot);
  const [startfehler, setStartfehler] = useState<string | null>(null);
  const [blatt, setBlatt] = useState<'zu' | 'kolonne' | 'meldungen'>('zu');
  const [ausrichtung, setAusrichtung] = useState<MapOrientation>('north');
  const [folgen, setFolgen] = useState<FollowMode | null>(null);
  /**
   * Läuft gerade ein kurzer Blick auf die Führung?
   *
   * Während einer Navigation ist „Führungsfahrzeug zeigen" kein Zustand,
   * sondern ein Blick: Wer die Spitze sucht, während er geführt wird, will sie
   * sehen — und dann weiterfahren, ohne noch eine Fläche zu treffen. Nach
   * `MAP_FOLLOW.navigationGlanceMs` springt die Karte von selbst zurück.
   */
  const [blick, setBlick] = useState(false);
  const [kartenfehler, setKartenfehler] = useState<string | null>(null);
  /**
   * Wie viel die untere Fläche von der Karte verdeckt, in Pixeln.
   *
   * Gemessen statt geschätzt: Wie hoch sie ist, hängt davon ab, ob eine
   * Navigation läuft — dann steht dort eine Reihe mehr — und ob das Gerät quer
   * gehalten wird. Die Karte braucht die Zahl, um das eigene Fahrzeug darüber
   * zu halten (A-80).
   */
  const [untenVerdeckt, setUntenVerdeckt] = useState(0);
  const untenRef = useRef<HTMLDivElement | null>(null);
  /** Steht die Einladung im Kolonnenblatt offen? Während der Fahrt nicht. */
  const [einladungOffen, setEinladungOffen] = useState(false);
  const [übergabeOffen, setÜbergabeOffen] = useState(false);
  const [, tick] = useState(0);
  const ansage = useMemo(() => new Ansage(), []);
  /** Steht die Zustandsleiste offen? Die Wahl überdauert die Fahrt. */
  const [leisteOffen, setLeisteOffenState] = useState(() => leseLeisteOffen());
  /** Gelesen beim Öffnen der Fahrt; umgeschaltet wird auf der Startseite (A-139). */
  const [pruefmodus] = useState(() => pruefmodusAn());
  /** Die Rolle aus der gespeicherten Sitzung — nur bis der Server antwortet. */
  const [gemerkteRolle] = useState<ParticipantRole | null>(
    () => loadSession(tourId)?.role ?? null,
  );
  const [ansageAn, setAnsageAn] = useState(ansage.an);
  /** Was zuletzt gesagt wurde — damit nichts zweimal kommt. */
  const zuletztGesagt = useRef<string | null>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      tick((n) => n + 1);
    }, 1_000);
    return () => {
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    const sitzung = loadSession(tourId);
    if (sitzung === null) {
      navigate('/beitreten');
      return;
    }

    let abgebrochen = false;

    void (async () => {
      try {
        const zustand = await api.state(tourId, sitzung.sessionToken);
        if (!abgebrochen) {
          await drive.start(sitzung, zustand.settings);
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          forgetSession(tourId);
          navigate('/beitreten');
          return;
        }
        setStartfehler(
          error instanceof ApiError ? error.message : 'Die Ausfahrt ließ sich nicht laden.',
        );
      }
    })();

    return () => {
      abgebrochen = true;
      drive.stop();
    };
  }, [tourId]);

  const ich = state.participants.find((p) => p.id === state.youId) ?? null;

  /*
   * Die eigene Rolle — und was gilt, solange der Server noch nicht geantwortet hat?
   *
   * Beim Öffnen der App ist die Teilnehmerliste noch leer. Ohne einen Anhalt
   * wäre man in diesem Augenblick „nicht die Führung", und die Fahrtansicht
   * zeigte die Bedienung der falschen Rolle: Dem Führungsfahrzeug blitzte
   * „Navigation zum Führungsfahrzeug" auf und verschwand wieder, sobald der
   * Zustand eintraf.
   *
   * Der Anhalt ist die zuletzt bekannte Rolle aus der gespeicherten Sitzung.
   * Sie liegt sofort vor, auch ohne Empfang, und wird von der Wahrheit des
   * Servers abgelöst, sobald sie eintrifft. Fehlt auch sie — bei einer Sitzung
   * aus einer früheren Fassung —, bleibt zurückhaltend, was von der Rolle
   * abhängt, statt zu raten.
   */
  const rolle: ParticipantRole | null = ich?.role ?? gemerkteRolle;
  const binFührung = rolle === 'leader';
  /** Ist gerade eine Rollenänderung für dieses Gerät unterwegs? */
  const wechselLäuft = state.rollenwechselFuer === state.youId && state.youId !== null;
  const rolleBekannt = rolle !== null;

  /*
   * Die Navigation — Stufe 2.
   *
   * Der Server schickt die Route und rechnet sie nach, sobald die Führung
   * weitergefahren ist. Wo der Fahrer auf ihr steht, rechnet dieses Gerät aus
   * seiner eigenen Messung: Die Angabe „in 300 Metern rechts" muss im selben
   * Augenblick stimmen und darf nicht an einer Verbindung hängen.
   */
  const navigation = state.navigationRoute;

  /*
   * Für die **Karte** zählt die eigene Messung, sobald sie frischer ist als das,
   * was vom Server zurückkam. Im Funkloch kommt von dort nichts, und der eigene
   * Punkt stand still, während das Gerät bei 61 km/h fuhr (A-106).
   *
   * Nur für die Karte: Die Kolonnenliste bekommt weiter `state.vehicles`, denn
   * dort stehen Zustand und Rückstand, und die rechnet der Server.
   */
  const eigeneSicht = useMemo(
    () =>
      withOwnMeasurement(
        state.vehicles,
        state.lastReportAt,
        state.youId,
        state.ownSample,
        state.ownSampleAt,
      ),
    [state.vehicles, state.lastReportAt, state.youId, state.ownSample, state.ownSampleAt],
  );

  /*
   * Der Blick endet von selbst — und auch dann, wenn die Navigation vorher
   * aufhört. Sonst bliebe die Karte auf der Führung stehen, obwohl der Anlass
   * für den Blick weggefallen ist.
   */
  /*
   * Die Höhe der unteren Fläche nachhalten.
   *
   * Ein `ResizeObserver` statt einer einmaligen Messung: Sie ändert sich beim
   * Drehen des Geräts, beim Aufklappen der Zustandsleiste und immer dann, wenn
   * eine Navigation beginnt oder endet. Eine Zahl, die nur beim ersten Bild
   * stimmt, wäre schlimmer als keine — sie sähe richtig aus.
   */
  useEffect(() => {
    const el = untenRef.current;
    if (el === null || typeof ResizeObserver === 'undefined') {
      return;
    }
    const messen = (): void => {
      setUntenVerdeckt(el.getBoundingClientRect().height);
    };
    messen();
    const beobachter = new ResizeObserver(messen);
    beobachter.observe(el);
    return () => {
      beobachter.disconnect();
    };
  }, []);

  const navigationLäuft = navigation !== null;
  useEffect(() => {
    if (!blick) {
      return;
    }
    if (!navigationLäuft) {
      setBlick(false);
      return;
    }
    const uhr = setTimeout(() => setBlick(false), MAP_FOLLOW.navigationGlanceMs);
    return () => clearTimeout(uhr);
    /*
     * Abhängig von `navigationLäuft`, **nicht** von `navigation`.
     *
     * Die Route ist alle fünf Sekunden ein neues Objekt — der Server rechnet sie
     * nach, sobald die Führung weitergefahren ist. Hing der Effekt daran, wurde
     * die Frist bei jeder neuen Route abgeräumt und neu gestartet. Acht Sekunden
     * Blick gegen fünf Sekunden Routentakt: Sie lief **nie** ab.
     *
     * Am 28.08.2026 auf der Ausfahrt „Henglarn" blieb der Blick deshalb hängen.
     * Weil er in der Rangfolge über allem außer `free` steht, wirkte danach auch
     * das Fadenkreuz nicht mehr, und „Führungsfahrzeug zeigen" blieb gedrückt.
     */
  }, [blick, navigationLäuft]);
  /**
   * Stehe ich gerade an einem Treffpunkt? Und wenn ja, wer fehlt noch?
   *
   * Die einzige Frage, die am Sammelpunkt zählt (Entwurf, Abschnitt 6). Sie
   * wird hier gestellt, sobald das eigene Fahrzeug im Umkreis liegt — nicht nur
   * für die Führung: Wer zurückliegt, will ebenso wissen, ob die anderen schon
   * warten.
   */
  const amTreffpunkt = useMemo(() => {
    if (state.ownSample === null || state.waypoints.length === 0) {
      return null;
    }
    const meine = new Set(
      state.arrivals.filter((a) => a.participantId === state.youId).map((a) => a.waypointId),
    );
    return treffpunktHier(
      state.ownSample,
      state.waypoints,
      state.settings.arrivalRadiusM,
      meine,
    );
  }, [state.ownSample, state.waypoints, state.arrivals, state.youId, state.settings]);

  const wartende = useMemo(() => {
    if (amTreffpunkt === null) {
      return null;
    }
    /*
     * Wer **ausgestiegen** ist, gehört nicht dazu. `tour_state` führt ihn
     * weiter in der Liste — die Kolonnenansicht braucht ihn, um zu zeigen, dass
     * er weg ist. Hier zählte er sonst für den Rest des Tages als fehlend, und
     * die Kolonne wartete auf jemanden, der zu Hause ist.
     *
     * Wer sich unsichtbar geschaltet hat, ebenso wenig: Auch das ist eine
     * ausdrückliche Abmeldung.
     *
     * Wer dagegen **kein Signal** mehr hat, gehört sehr wohl dazu — genau auf
     * den wird gewartet. Das ist der Kern der Frage „wer fehlt noch?".
     */
    const dabei = state.participants
      .filter((t) => t.visible && t.connectionState !== 'left')
      .map((t) => t.id);
    return anwesenheit(dabei, state.arrivals, amTreffpunkt.id);
  }, [amTreffpunkt, state.participants, state.arrivals]);

  const fortschritt = useMemo(
    () =>
      navigation === null || state.ownSample === null
        ? null
        : progressAlongRoute(navigation, state.ownSample),
    [navigation, state.ownSample],
  );

  useEffect(() => {
    if (navigation === null || fortschritt === null) {
      return;
    }
    const nächste = announcementFor(navigation, fortschritt, zuletztGesagt.current);
    if (nächste === null) {
      return;
    }
    zuletztGesagt.current = nächste.key;
    ansage.sprich(nächste.text);
  }, [navigation, fortschritt, ansage]);

  useEffect(() => {
    if (navigation === null) {
      zuletztGesagt.current = null;
    }
  }, [navigation]);

  useEffect(() => {
    if (state.navigationEnded === 'arrived') {
      ansage.sprich('Du bist wieder bei der Kolonne.');
    }
  }, [state.navigationEnded, ansage]);

  /*
   * Zwei Hinweise schließen sich von selbst — beide bestätigen nur, was ohnehin
   * gut ist: dass die Standortübertragung wieder läuft, und dass das Fahrzeug
   * wieder bei der Kolonne ist. Am Steuer dafür zu tippen, ist eine Zumutung
   * ohne Zweck. Alles, was eine Störung meldet, bleibt stehen.
   */
  useSelbstschluss(state.hintSelfClearing ? state.hint : null, () => {
    drive.clearHint();
  });

  useSelbstschluss(
    state.navigationEnded !== null && NAVIGATION_END_SELF_CLEARING.has(state.navigationEnded)
      ? state.navigationEnded
      : null,
    () => {
      drive.clearNavigationEnd();
    },
  );
  /**
   * Wer bliebe, wenn dieses Fahrzeug ginge.
   *
   * Grundlage von zweierlei: der Liste möglicher Nachfolger für die Führung und
   * der Frage, ob mit dem Verlassen die ganze Ausfahrt endet. Dieselbe Regel
   * steht im Server (`planLeave`) — dort ist sie verbindlich, hier bestimmt sie
   * nur, was auf dem Bildschirm steht.
   */
  const verbliebene = state.participants.filter(
    (p) => p.id !== state.youId && p.connectionState !== 'left',
  );
  /**
   * Die Warnungen, die diesen Fahrer etwas angehen.
   *
   * Den Empfängerkreis bestimmt der Server; hier wird dieselbe Regel noch
   * einmal angelegt, damit auch eine Warnung aus einer älteren Sitzung nicht
   * am falschen Platz landet. Sie steht in packages/shared und nirgends
   * sonst — Führung und Schlusswagen sehen die Lage, niemand die Warnung über
   * sich selbst.
   */
  const sichtbareWarnungen = state.warnings.filter((warnung) =>
    receivesWarning(ich?.role ?? 'participant', warnung.participantId === state.youId),
  );
  const meinFahrzeug = state.vehicles.find((f) => f.participantId === state.youId) ?? null;

  /*
   * Wann „Navigation zum Führungsfahrzeug" auf dem Bildschirm steht.
   *
   * Zunächst stand die Fläche dauerhaft da — mit der Begründung, der Fahrer
   * merke als Erster, dass er falsch ist. Gemessen stimmt das nur für wenige
   * Sekunden: Bei 150 m Abstand zur Spur erkennt der Server „abseits der
   * Strecke", und die sind bei 50 km/h nach elf Sekunden erreicht, im Ort nach
   * achtzehn. Auf zurückgefallen erkennt er nach der eingestellten Schwelle.
   *
   * Dafür dauerhaft eine Reihe zu belegen, ist der falsche Tausch: Was immer
   * dasteht, kostet Karte — und die Karte ist das, was während der Fahrt
   * gebraucht wird. Die Fläche erscheint deshalb, sobald der eigene Zustand
   * einen Anlass gibt, und dann groß.
   *
   * Welche Zustände das sind, entscheidet `navigationsAnlass` — und nur die:
   * `paused`, `no_signal` und der Anschluss selbst geben keinen. Ohne bekannten
   * Zustand — vor der ersten Auswertung — erscheint nichts; geraten wird nicht.
   */
  /*
   * Alles, was die Zustandsleiste zeigt, an einer Stelle — sie und der
   * Zustandsknopf müssen dasselbe zählen.
   */
  const gerätezustand: Gerätezustand = {
    verbindung: state.connection,
    wachhalten: state.wakeLock,
    wachhaltenGrund: state.wakeLockError,
    gepuffert: state.buffered,
    gepufferteMeldungen: state.bufferedStatus,
    pausiert: state.pageVisibility === 'background',
    unsichtbar: ich?.visible === false,
    geraetOfflineSeit: pruefmodus ? state.geraetOfflineSeit : null,
  };
  const störungen = zähleStörungen(gerätezustand);

  const eigenerZustand = meinFahrzeug?.convoyState ?? null;

  /**
   * Der Anlass, aus dem die Navigationsfläche dasteht — ihre zweite Zeile.
   *
   * `null` heißt: kein Anlass, keine Fläche. Welche Zustände dazugehören und
   * warum, steht bei `navigationsAnlass`.
   */
  const anlass = navigationsAnlass(eigenerZustand);

  /*
   * Dieselbe Entscheidung, eine Stelle: Gibt es einen Anlass, steht die Fläche
   * da und trägt ihn als zweite Zeile. Gibt es keinen, entfällt sie. Welche
   * Zustände keinen Anlass geben und warum, steht bei `navigationsAnlass`.
   */
  const brauchtNavigation = anlass !== null;
  const joinCodeOderLeer = state.tour?.joinCode ?? '';
  const dunkel = istDunkel();

  /*
   * Was die Karte gerade zeigt.
   *
   * Vier Fälle, in dieser Reihenfolge:
   *
   * 1. Der Fahrer hat die Karte selbst verschoben — dann gehört sie ihm, auch
   *    während einer Navigation. Sie ihm wegzuziehen wäre übergriffig.
   * 2. Ein kurzer Blick auf die Führung läuft.
   * 3. Eine Navigation läuft — dann die Navigationsansicht: das eigene Fahrzeug
   *    unten, der Platz darüber für den Weg.
   * 4. Sonst die Vorgabe der Rolle: Wer führt, will die ganze Kolonne sehen;
   *    wer hinterherfährt, sich selbst.
   *
   * `folgen` bleibt dabei unberührt. Endet die Navigation, steht dort noch die
   * Wahl, die der Fahrer vorher getroffen hat — und genau dorthin kehrt die
   * Karte zurück.
   */
  const kartenführung = kartenführungFür({
    folgen,
    blick,
    navigationLäuft: navigation !== null,
    binFührung,
  });

  /*
   * Welche Meldungen die eigene Rolle absetzen darf.
   *
   * „Bitte langsamer" richtet sich an die Führung — die sendet es nicht an sich
   * selbst; „Wir halten an" darf nur sie. Die Regel steht in shared, hier wird
   * sie nur angezeigt. Durchgesetzt wird sie am Server: Auf eine Oberfläche,
   * die bestimmte Flächen weglässt, kann man sich nicht verlassen.
   */
  const meineRolle = ich?.role ?? 'participant';
  const meldearten = (Object.keys(STATUS_LABELS) as StatusMessageKind[]).filter((kind) =>
    maySendStatusMessage(kind, meineRolle),
  );

  /*
   * Die Meldungen nach ihrem Empfängerkreis getrennt.
   *
   * Vor dem Tippen zählt genau eine Frage: Wer liest das gleich? Standen alle
   * Flächen in einem Block, war das der Anzeige nicht zu entnehmen — „Panne"
   * geht an die ganze Kolonne, „muss tanken" nur nach vorn, und beide sahen
   * gleich aus.
   *
   * „An alle" steht oben. Für die Führung sind das ihre Ansagen, für alle
   * anderen die Panne — in beiden Fällen das Dringlichste.
   */
  // Innerhalb einer Gruppe steht vorn, was nur die eigene Rolle absetzen darf:
  // für die Führung ihre Ansagen, die sie am häufigsten braucht.
  const eigeneZuerst = (arten: readonly StatusMessageKind[]): StatusMessageKind[] =>
    [...arten].sort(
      (a, b) =>
        (STATUS_MESSAGE_RULES[a].sender === meineRolle ? 0 : 1) -
        (STATUS_MESSAGE_RULES[b].sender === meineRolle ? 0 : 1),
    );

  const anAlle = eigeneZuerst(
    meldearten.filter((kind) => STATUS_MESSAGE_RULES[kind].audience === 'all'),
  );
  const nachVorn = eigeneZuerst(
    meldearten.filter((kind) => STATUS_MESSAGE_RULES[kind].audience !== 'all'),
  );

  // Gibt es einen Schlusswagen, liest er mit — dann muss die Überschrift ihn
  // auch nennen. Eine Ausfahrt ohne Schlusswagen ist zulässig.
  const mitSchlusswagen = state.participants.some(
    (p) => p.role === 'sweeper' && p.connectionState !== 'left',
  );

  /**
   * Wie viel steht gerade über der Bedienleiste?
   *
   * Blatt und Meldebereich teilen sich denselben Platz am unteren Rand. Läge
   * beides übereinander, verdeckte das eine das andere — und was gerade
   * hereingekommen ist, hätte dabei die schlechteren Karten: Es erschiene
   * hinter einer Liste, die jemand vor Sekunden aufgeklappt hat.
   *
   * Deshalb schließt sich ein offenes Blatt, sobald etwas eingeht, und lässt
   * sich erst wieder öffnen, wenn alles weggetippt ist. Ein Tipp mehr — dafür
   * steht nie eine Meldung hinter einer Liste.
   */
  const offeneMeldungen =
    sichtbareWarnungen.length +
    (state.navigationRequestedAt !== null ? 1 : 0) +
    state.incomingStatus.length +
    (kartenfehler !== null ? 1 : 0) +
    (state.hint !== null ? 1 : 0);

  const etwasSteht = offeneMeldungen > 0;

  /*
   * Die jüngste eigene Ansage, deren Empfang zurückgemeldet wird.
   *
   * Nur für die Führung: Wer „Wir halten an" schickt, muss sehen, wer es
   * gelesen hat — und vor allem, wer nicht.
   */
  const eigeneAnsage =
    state.statusMessages.find(
      (meldung) => meldung.participantId === state.youId && isAcknowledged(meldung.kind),
    ) ?? null;

  // Kommt etwas herein, während ein Blatt offen steht, schließt es sich.
  useEffect(() => {
    if (etwasSteht) {
      setBlatt('zu');
    }
  }, [etwasSteht]);

  if (startfehler !== null) {
    return (
      <div className="seite">
        <h1>Ausfahrt</h1>
        <p className="hinweis hinweis--alarm">{startfehler}</p>
        <button
          type="button"
          className="knopf"
          onClick={() => {
            navigate('/');
          }}
        >
          Zur Startseite
        </button>
      </div>
    );
  }

  return (
    <div className="fahrt">
      <ConvoyMap
        vehicles={eigeneSicht.vehicles}
        participants={state.participants}
        lastReportAt={eigeneSicht.lastReportAt}
        youId={state.youId}
        track={state.track}
        route={navigation?.shape ?? null}
        plannedRoute={state.routePlan?.points ?? null}
        waypoints={state.waypoints}
        nextWaypointId={state.arrival?.waypoint.id ?? null}
        orientation={ausrichtung}
        follow={kartenführung}
        ownSpeedMps={state.ownSample?.speedMps ?? null}
        bottomOverlayPx={untenVerdeckt}
        dark={dunkel}
        onUserPan={() => {
          setFolgen('free');
        }}
        onMapError={(meldung) => {
          setKartenfehler(meldung);
        }}
      />

      {/*
        Oben steht nur, was man ansieht, aber nicht bedient. Das Gerät hängt in
        der Halterung, bedient wird mit dem Daumen — und der reicht an den
        oberen Bildschirmrand nicht heran.
      */}
      <div className="ueberlagerung">
        <div className="ueberlagerung__reihe">
          {/* Zugeklappt bleibt zugeklappt — auch wenn unterwegs etwas dazukommt.
              Wer sie weggeklappt hat, will die Karte sehen; der Knopf wird rot
              und trägt die Zahl, das ist unübersehbar genug. */}
          {leisteOffen && (
            <Zustandsleiste
              verbindung={gerätezustand.verbindung}
              wachhalten={gerätezustand.wachhalten}
              wachhaltenGrund={gerätezustand.wachhaltenGrund}
              gepuffert={gerätezustand.gepuffert}
              gepufferteMeldungen={gerätezustand.gepufferteMeldungen}
              pausiert={gerätezustand.pausiert}
              unsichtbar={gerätezustand.unsichtbar}
              geraetOfflineSeit={gerätezustand.geraetOfflineSeit}
            />
          )}
          {/* Läuft eine Navigation, stehen Tempo und Richtung in ihrer
              Fußzeile — zwei Kästen übereinander wären derselbe Inhalt auf
              doppelter Fläche. */}
          {navigation === null && (
            <Fahrtwerte messung={state.ownSample} gemessenAm={state.ownSampleAt} />
          )}
        </div>

        {/*
          Der nächste Treffpunkt — nur, wenn keine Navigation läuft.
          Wer zurückgeführt wird, hat eine dringendere Frage als die nach dem
          Mittagessen, und zwei Balken übereinander sind auf einem Telefon in
          der Halterung einer zu viel.
        */}
        {navigation === null &&
          (amTreffpunkt !== null && wartende !== null ? (
            /*
              Am Treffpunkt zählt nicht mehr, wie weit es noch ist — man ist da.
              Die Leiste wechselt deshalb, statt eine zweite danebenzustellen.
            */
            <Ankunftsbalken
              waypoint={amTreffpunkt}
              anwesend={wartende}
              teilnehmer={state.participants}
            />
          ) : (
            state.arrival !== null && <Treffpunktbalken schaetzung={state.arrival} />
          ))}

        {/*
          Der Abbiegehinweis steht oben und wird nicht angefasst: Das Gerät hängt
          in der Halterung, und was der Daumen erreichen muss — beenden, Ansage —
          sitzt unten bei den übrigen Flächen.
        */}
        {navigation !== null && (
          <Navigationsbalken
            route={navigation}
            fortschritt={fortschritt}
            messung={state.ownSample}
            gemessenAm={state.ownSampleAt}
          />
        )}
      </div>

      {/*
        Der Fuß trägt alles, was angefasst wird: Blatt, Meldungen, Bedienung.
        Er sitzt am unteren Rand, und weil er von dort nach oben wächst,
        verschiebt eine neu eingehende Meldung die Schaltflächen nicht — wer
        gerade tippt, trifft weiterhin, was er treffen wollte.
      */}
      <div className="fahrt__fuss">
        {blatt === 'kolonne' && (
          <Blatt>
            {/*
              Der Name der Ausfahrt.

              Nach dem Beitritt tauchte er nie wieder auf — wer zwei Ausfahrten
              hintereinander fährt oder das Gerät aus der Tasche holt, wusste
              nicht, in welcher er steckt. Hier steht er, ohne Platz auf der
              Karte zu kosten.
            */}
            {state.tour !== null && (
              <p className="blattkopf">
                {state.tour.name}
                {state.tour.state === 'paused' && ' · pausiert'}
              </p>
            )}

            {eigeneAnsage !== null && (
              <p className="datenschutz" style={{ margin: '0 0 8px' }}>
                <strong>„{STATUS_LABELS[eigeneAnsage.kind].text}"</strong> —{' '}
                {eigeneAnsage.acknowledgedBy.length} von{' '}
                {
                  /*
                   * Gezählt wird, wen die Ansage überhaupt erreichen konnte.
                   *
                   * Wer erst danach beigetreten ist, zählt nicht mit — sonst
                   * wartete die Führung auf eine Lesebestätigung von jemandem,
                   * der zu der Zeit nicht in der Kolonne war, und die Zahl
                   * würde nie voll.
                   */
                  state.participants.filter(
                    (p) =>
                      p.connectionState !== 'left' &&
                      p.id !== state.youId &&
                      presentWhenSent(p.joinedAt, eigeneAnsage.createdAt),
                  ).length
                }{' '}
                haben es gelesen.
              </p>
            )}
            <Kolonne
              participants={state.participants}
              vehicles={state.vehicles}
              lastReportAt={state.lastReportAt}
              youId={state.youId}
              binFührung={binFührung}
              gelesenVon={eigeneAnsage?.acknowledgedBy ?? null}
              ansageAb={eigeneAnsage?.createdAt ?? null}
              onEntfernen={(id) => {
                drive.removeParticipant(id);
              }}
              onFührungÜbergeben={(id) => {
                drive.changeRole(id, 'leader');
              }}
            />
            {/*
              Den Schlusswagen benennt sich jeder selbst.
              
              Wer hinten fährt, weiß das — die Führung ist mit Fahren
              beschäftigt und sieht das Ende der Kolonne oft gar nicht. Eine
              Ausfahrt ohne Schlusswagen bleibt zulässig; deshalb lässt sich die
              Rolle jederzeit wieder ablegen, und nichts verlangt nach ihr.

              Die Führung sieht diese Fläche nicht: Sie kann nicht zugleich
              vorn und hinten fahren.
            */}
            {!binFührung && (
              <button
                type="button"
                className="knopf knopf--leise"
                style={{ marginTop: 10 }}
                /*
                 * Gesperrt in zwei Fällen, und sie meinen Verschiedenes.
                 *
                 * **Unsichtbar** — als Schlusswagen müssen die anderen einen
                 * auf der Karte sehen. Abgewiesen wird es ohnehin am Server.
                 *
                 * **Ein Wechsel ist unterwegs** — dann steht „Einen Augenblick
                 * …" darauf. Ohne das sah die Fläche nach dem Tippen aus wie
                 * vorher, und ein zweiter Tipp traf die andere Verzweigung und
                 * nahm die eben übernommene Rolle wieder weg. Am 30.08.2026 auf
                 * der Ausfahrt 242008 bestand ein Schlusswagen dadurch 1,02
                 * Sekunden.
                 */
                disabled={ich?.visible === false || wechselLäuft}
                onClick={() => {
                  if (ich?.role === 'sweeper') {
                    drive.changeRole(state.youId ?? '', 'participant');
                    return;
                  }
                  const bisher = state.participants.find(
                    (p) => p.role === 'sweeper' && p.connectionState !== 'left',
                  );
                  if (
                    bisher !== undefined &&
                    !confirm(`${bisher.displayName} fährt als Schlusswagen. Übernehmen?`)
                  ) {
                    return;
                  }
                  drive.changeRole(state.youId ?? '', 'sweeper');
                }}
              >
                {wechselLäuft
                  ? 'Einen Augenblick …'
                  : ich?.role === 'sweeper'
                    ? 'Nicht mehr Schlusswagen'
                    : 'Ich fahre als Schlusswagen'}
              </button>
            )}

            <button
              type="button"
              className="knopf knopf--leise"
              style={{ marginTop: 10 }}
              onClick={() => {
                void drive.setVisible(ich?.visible === false);
              }}
            >
              {ich?.visible === false
                ? 'Wieder sichtbar werden'
                : 'Für die anderen unsichtbar werden'}
            </button>

            {/* Kein Rückfragefenster, aber auch kein stiller Verlust: Wer die
                Rolle hält, erfährt vorher, dass er sie damit abgibt. */}
            {ich?.role === 'sweeper' && (
              <p className="datenschutz knopfhinweis">
                Damit gibst du den Schlusswagen ab.
              </p>
            )}

            {/*
              Andere nachholen — auch mitten aus der Kolonne heraus.

              Bisher gab es Beitrittscode und QR-Code nur einmal, beim Anlegen.
              Wer sie nicht abfotografiert hatte, konnte niemanden mehr
              hereinholen; und wer über den QR-Code beigetreten war, hat den
              Code nie zu sehen bekommen. Dabei stößt genau das unterwegs
              vor — jemand kommt später dazu.

              Hier unten, unmittelbar über dem Verlassen: Beides betrifft die
              Zugehörigkeit zur Ausfahrt — wer kommt dazu, und wer geht.

              Eingeklappt, weil es während der Fahrt nichts zu suchen hat.
            */}
            {state.tour !== null && (
              <>
                <button
                  type="button"
                  className="knopf knopf--leise"
                  style={{ marginTop: 10 }}
                  onClick={() => {
                    setEinladungOffen((offen) => !offen);
                  }}
                >
                  {einladungOffen ? 'Einladung ausblenden' : 'Andere einladen'}
                </button>
                {einladungOffen && (
                  <div style={{ marginTop: 10 }}>
                    <Einladung joinCode={state.tour.joinCode} />
                    <button
                      type="button"
                      className="knopf knopf--leise"
                      onClick={() => {
                        void navigator.clipboard?.writeText(beitrittsUrl(joinCodeOderLeer));
                      }}
                    >
                      Link kopieren
                    </button>
                  </div>
                )}
              </>
            )}

            {/*
              Verlassen — und für die Führung erst nach der Übergabe.

              Bis zum 15.08.2026 nahm die Führung ihre Rolle einfach mit: In der
              Datenbank blieb ein Führungsfahrzeug stehen, das weg war, der
              Teilindex hielt den Platz besetzt, und nachrücken durfte niemand.
              Die Kolonne blieb kopflos zurück, ihre Spur wuchs nicht mehr, und
              aus der Anwendung heraus war das nicht mehr zu heilen.

              Kein Automatismus, der jemanden ungefragt zur Führung macht: Sie
              wählt ihren Nachfolger, so wie sie die Führung sonst auch übergibt.
            */}
            <button
              type="button"
              className="knopf knopf--leise"
              style={{ marginTop: 10 }}
              onClick={() => {
                // Der Letzte macht das Licht aus — dann gibt es niemanden, an
                // den zu übergeben wäre.
                if (binFührung && verbliebene.length > 0) {
                  setÜbergabeOffen((offen) => !offen);
                  return;
                }

                const frage =
                  verbliebene.length === 0
                    ? 'Du bist der Letzte in der Kolonne. Mit dem Verlassen ist die Ausfahrt beendet. Fortfahren?'
                    : 'Die Ausfahrt wirklich verlassen? Deine Position wird danach nicht mehr geteilt.';
                if (confirm(frage)) {
                  void drive.leave().then(() => {
                    forgetSession(tourId);
                    navigate('/');
                  });
                }
              }}
            >
              {übergabeOffen ? 'Doch nicht verlassen' : 'Ausfahrt verlassen'}
            </button>

            {übergabeOffen && (
              <div className="uebergabe">
                <p className="datenschutz" style={{ margin: '0 0 8px' }}>
                  Die Führung wird übergeben, nicht abgelegt — ohne sie hätte die
                  Kolonne keine Spur. An wen?
                </p>
                {verbliebene.map((teilnehmer) => (
                  <button
                    key={teilnehmer.id}
                    type="button"
                    className="knopf knopf--leise"
                    style={{ marginTop: 8 }}
                    onClick={() => {
                      void drive.leave(teilnehmer.id).then(() => {
                        forgetSession(tourId);
                        navigate('/');
                      });
                    }}
                  >
                    An {teilnehmer.displayName} ({teilnehmer.licensePlate}) übergeben und verlassen
                  </button>
                ))}
              </div>
            )}

            {/*
              Das Ende der Ausfahrt — nur für die Führung.

              Es gab diesen Knopf nirgends, und die Schnittstelle dahinter lag
              seit dem ersten Tag unbenutzt. Damit wurde keine Ausfahrt je
              beendet: Der Beitrittscode verfiel nie, und die Löschfrist für die
              Positionsdaten begann nie zu laufen. Die Zusage aus dem
              Beitrittshinweis stand auf dem Bildschirm und wurde nicht
              eingelöst.
            */}
            {binFührung && !übergabeOffen && (
              <button
                type="button"
                className="knopf knopf--leise"
                style={{ marginTop: 10 }}
                onClick={() => {
                  if (
                    confirm(
                      'Die Ausfahrt für alle beenden? Der Beitrittscode verfällt, und alle Positionsdaten werden 24 Stunden später gelöscht.',
                    )
                  ) {
                    void drive.finishTour().then((beendet) => {
                      if (beendet) {
                        forgetSession(tourId);
                        navigate('/');
                      }
                    });
                  }
                }}
              >
                Ausfahrt für alle beenden
              </button>
            )}
          </Blatt>
        )}

        {blatt === 'meldungen' && (
          <Blatt>
            {[
              { titel: 'Sehen alle', arten: anAlle },
              {
                titel: mitSchlusswagen
                  ? 'Sehen nur Führung und Schlusswagen'
                  : 'Sieht nur das Führungsfahrzeug',
                arten: nachVorn,
              },
            ]
              .filter((gruppe) => gruppe.arten.length > 0)
              .map((gruppe) => (
                <div key={gruppe.titel}>
                  <p className="blattgruppe">{gruppe.titel}</p>
                  <div className="meldeliste">
                    {gruppe.arten.map((kind) => (
                      <button
                        key={kind}
                        type="button"
                        className="knopf"
                        onClick={() => {
                          drive.sendStatus(kind);
                          setBlatt('zu');
                        }}
                      >
                        <MeldungsZeichen kind={kind} />
                        {STATUS_LABELS[kind].text}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            {state.statusMessages.length > 0 && (
              <>
                <h2>Zuletzt gemeldet</h2>
                <ul className="liste">
                  {state.statusMessages.slice(0, 5).map((meldung) => {
                    const wer = state.participants.find((p) => p.id === meldung.participantId);
                    return (
                      <li className="eintrag" key={meldung.id}>
                        <MeldungsZeichen kind={meldung.kind} />
                        <div className="eintrag__text">
                          <div className="eintrag__name">{STATUS_LABELS[meldung.kind].text}</div>
                          <div className="eintrag__unten">
                            {wer?.displayName ?? 'Unbekannt'} ·{' '}
                            {formatAge(Date.now() - meldung.createdAt)}
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </Blatt>
        )}

        <div className="meldebereich">
          {/* Wer den Anschluss verliert, erscheint hier — breit und unübersehbar. */}
          {sichtbareWarnungen.map((warnung) => {
            const wer = state.participants.find((p) => p.id === warnung.participantId);
            const label = CONVOY_LABELS[warnung.convoyState];
            return (
              <div className="warnbalken" key={warnung.participantId} role="alert">
                <div className="warnbalken__kopf">
                  <ZustandsZeichen state={warnung.convoyState} />
                  <span>
                    {wer?.displayName ?? 'Ein Fahrzeug'}: {label.text}
                  </span>
                </div>
                <p style={{ margin: '0 0 0.6rem' }}>
                  {label.erklaerung}
                  {warnung.delaySeconds !== null &&
                    ` Rückstand ${formatDelay(warnung.delaySeconds)}.`}
                </p>
                <div className="reihe">
                  {/*
                    Eine Navigation anstoßen darf nur die Führung — der
                    Schlusswagen sieht die Lage, entscheidet aber nicht.

                    Bei „Kein Signal" steht die Fläche gar nicht erst da. Die
                    Anfrage geht an die Verbindung des Betroffenen, und die gibt
                    es dann per Definition nicht: Der Server schickt ins Leere,
                    der Balken verschwindet, und nichts geschieht. Eine Fläche,
                    die nichts bewirkt, ist schlimmer als keine — der Fahrer
                    hielte die Sache für erledigt.

                    Bei den übrigen Warnzuständen bleibt sie: Wer zurückgefallen
                    oder falsch abgebogen ist, meldet sich ja weiter.
                  */}
                  {binFührung && warnung.convoyState !== 'no_signal' && (
                    <button
                      type="button"
                      className="knopf knopf--alarm"
                      onClick={() => {
                        drive.requestNavigationFor(warnung.participantId);
                        drive.dismissWarning(warnung.participantId);
                      }}
                    >
                      Navigation starten
                    </button>
                  )}
                  <button
                    type="button"
                    className="knopf"
                    onClick={() => {
                      drive.dismissWarning(warnung.participantId);
                    }}
                  >
                    Gesehen
                  </button>
                </div>
              </div>
            );
          })}

        {state.navigationRequestedAt !== null && navigation === null && (
          <div className="warnbalken" role="alert">
            <div className="warnbalken__kopf">Das Führungsfahrzeug ruft dich zu sich.</div>
            <div className="reihe">
              <button
                type="button"
                className="knopf knopf--alarm"
                onClick={() => {
                  // Die Stimme muss aus dem Fingertipp heraus anspringen — siehe
                  // die Fläche für dieselbe Handlung weiter unten.
                  ansage.weckeStimme();
                  drive.startNavigation();
                  setBlatt('zu');
                }}
              >
                {NAVIGATION_STARTEN_KURZ}
              </button>
              <button
                type="button"
                className="knopf"
                onClick={() => {
                  setFolgen('all');
                  drive.clearNavigationRequest();
                }}
              >
                Nur zeigen
              </button>
            </div>
          </div>
        )}

        {state.navigationEnded !== null &&
          NAVIGATION_END_LABELS[state.navigationEnded] !== null && (
            <div className="hinweis hinweis--achtung hinweis--balken">
              {NAVIGATION_END_LABELS[state.navigationEnded]}
              <button
                type="button"
                className="knopf"
                onClick={() => {
                  drive.clearNavigationEnd();
                }}
              >
                Verstanden
              </button>
            </div>
          )}

        {kartenfehler !== null && (
          <div className="hinweis hinweis--alarm hinweis--balken">
            {kartenfehler}
            <button
              type="button"
              className="knopf"
              onClick={() => {
                setKartenfehler(null);
              }}
            >
              Verstanden
            </button>
          </div>
        )}

        {state.hint !== null && (
          <div className="hinweis hinweis--achtung hinweis--balken">
            {state.hint}
            <button
              type="button"
              className="knopf"
              onClick={() => {
                /*
                 * Wer nicht mehr zur Ausfahrt gehört, wird nicht mitten in der
                 * Fahrt weggeschaltet — er tippt selbst, dann geht es zurück
                 * zum Anfang. Die Karte bliebe sonst stehen und zeigte eine
                 * Kolonne, die ihn nichts mehr angeht.
                 */
                if (state.removed) {
                  forgetSession(tourId);
                  navigate('/');
                  return;
                }
                drive.clearHint();
              }}
            >
              Verstanden
            </button>
          </div>
        )}

          {/*
            Was ein anderes Fahrzeug meldet, tritt dem Fahrer entgegen — es
            wartet nicht darauf, dass jemand während der Fahrt eine Liste
            aufklappt. Weggetippt wird es einzeln; von selbst verschwindet es
            nicht, sonst hinge es davon ab, ob man gerade hingesehen hat.
          */}
          {state.incomingStatus.map((meldung) => {
            const wer = state.participants.find((p) => p.id === meldung.participantId);
            const label = STATUS_LABELS[meldung.kind];
            return (
              <div className="meldebalken" key={meldung.id} role="alert">
                <div className="meldebalken__text">
                  {/*
                    Wer es schickt, klein darüber; die Meldung groß darunter.
                    Gelesen werden muss im Vorbeischauen die Meldung.

                    Aber „MacBook" allein beantwortet die Frage nicht, die sich
                    bei einer Panne sofort stellt: **welches Auto ist das?**
                    Deshalb dieselben Angaben wie in der Kolonnenliste.

                    Zwei Zeilen, nicht eine: In einer Zeile brach schon auf dem
                    iPhone das Fahrzeug ab („MacBook · PB-D 4 · MacBook / 2…"),
                    und das Baujahr fehlte ganz. Oben steht, wer es ist — Name
                    und Kennzeichen; darunter, was er fährt. Das Kennzeichen
                    steht hervor: Es ist das Einzige, woran sich ein Fahrzeug
                    zweifelsfrei erkennen lässt.
                  */}
                  <div className="meldebalken__wer">
                    {/* Kürzen darf der Name, nicht das Kennzeichen — siehe styles.css. */}
                    <span className="meldebalken__name">{wer?.displayName ?? 'Ein Fahrzeug'}</span>
                    {wer !== undefined && wer.licensePlate !== '' && (
                      <span className="meldebalken__kennzeichen">{wer.licensePlate}</span>
                    )}
                  </div>
                  {wer !== undefined && fahrzeugText(wer) !== '' && (
                    <div className="meldebalken__fahrzeug">{fahrzeugText(wer)}</div>
                  )}
                  <div className="meldebalken__was">
                    <MeldungsZeichen kind={meldung.kind} />
                    <span>{label.text}</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="knopf knopf--quittung"
                  onClick={() => {
                    drive.dismissStatus(meldung.id);
                  }}
                >
                  Gesehen
                </button>
              </div>
            );
          })}
        </div>

      <div className="fahrt__unten" ref={untenRef}>
        {rolleBekannt && !binFührung && (
          <div className="rueckstand">
            <span className="rueckstand__zahl">
              {formatDelay(meinFahrzeug?.delaySeconds ?? null)}
            </span>
            {/* Fester Umbruch statt Umbruch nach Platz: So steht die
                Beschriftung bei jedem Wert gleich, und der Block behält seine
                Höhe. Siehe die Begründung an `.rueckstand` in styles.css. */}
            <span className="rueckstand__wort">
              Rückstand zum
              <br />
              Führungsfahrzeug
            </span>
          </div>
        )}

        {/* Beide Reihen als ein Block: Im Querformat rückt er neben den
            Rückstand, statt sich unter ihn zu stapeln. */}
        <div className="fahrt__bedienung">
        {/*
          Die Navigation zur Kolonne — Stufe 2.

          Nur für alle außer der Führung: Sie kann sich schlecht zu sich selbst
          führen lassen. Und nur, wenn der Anschluss nicht steht — die
          Begründung dafür steht oben bei `brauchtNavigation`. Läuft bereits
          eine Navigation, bleibt die Reihe stehen, bis sie endet: Sie trägt
          dann das Beenden und die Ansage.
        */}
        {rolleBekannt && !binFührung && (brauchtNavigation || navigation !== null) && (
          <div className="reihe">
            {navigation === null ? (
              <button
                type="button"
                className={`knopf knopf--wichtig${anlass === null ? '' : ' knopf--zweizeilig'}`}
                onClick={() => {
                  /*
                   * Die Stimme muss aus dem Fingertipp heraus anspringen: iOS
                   * lässt eine Sprachausgabe, die nicht unmittelbar aus einer
                   * Bedienhandlung hervorgeht, wortlos verstummen.
                   */
                  ansage.weckeStimme();
                  drive.startNavigation();
                  setBlatt('zu');
                }}
              >
                <span>{NAVIGATION_STARTEN}</span>
                {/*
                  Warum die Fläche gerade dasteht.

                  Der Anlass gehört zu dem, was er erklärt — stünde er oben beim
                  Rückstand, müsste sich der Fahrer den Zusammenhang selbst
                  denken. Höhe kostet es nicht: Die Fläche misst 92 Pixel, die
                  zweite Zeile passt in den vorhandenen Raum.

                  Dieselben Wörter, die auch die Führung sieht — „Steht",
                  „Zurückgefallen", „Abseits". Ein eigener Satz für
                  einen selbst wäre freundlicher, aber dann hätte dieselbe Sache
                  zwei Namen, je nachdem wer draufschaut.

                  Und es bleibt eine Begründung, keine Warnung über sich selbst:
                  Sie steht ruhig auf der Fläche, springt nicht auf und will
                  nicht weggetippt werden.

                  Mit „Grund:" davor, weil das Wort allein zweideutig ist: Unter
                  „Navigation zum Führungsfahrzeug" liest sich „Abseits" auch wie
                  eine Aussage über die Navigation. Das Wort selbst bleibt
                  dasselbe, das die Führung sieht — es bekommt nur eine
                  Beschriftung.
                */}
                {anlass !== null && <span className="knopf__zusatz">Grund: {anlass}</span>}
              </button>
            ) : (
              <>
                {/*
                  Die flachere Höhe, wie die unterste Reihe.
                  
                  Beide werden während der Fahrt selten gebraucht: Die
                  Navigation endet von selbst, sobald der Anschluss wieder
                  steht, und die Ansage schaltet man einmal. Die 28 Pixel, die
                  das je Reihe spart, gehören der Karte — bei laufender
                  Navigation steht hier eine Reihe mehr als sonst, und das
                  eigene Fahrzeug rutschte hinter die Rückstandsfläche.
                */}
                <button
                  type="button"
                  className="knopf knopf--leise knopf--alarm"
                  onClick={() => {
                    ansage.schweig();
                    drive.stopNavigation();
                  }}
                >
                  Navigation beenden
                </button>
                <button
                  type="button"
                  className="knopf knopf--leise knopf--symbol"
                  title={ansageAn ? 'Ansage aus' : 'Ansage an'}
                  aria-label={
                    ansageAn
                      ? 'Gesprochene Hinweise ausschalten'
                      : 'Gesprochene Hinweise einschalten'
                  }
                  disabled={!ansage.verfügbar}
                  onClick={() => {
                    const neu = !ansageAn;
                    ansage.setAn(neu);
                    setAnsageAn(neu);
                    if (neu) {
                      ansage.weckeStimme();
                    }
                  }}
                >
                  <ZeichenAnsage an={ansageAn} />
                </button>
              </>
            )}
          </div>
        )}
        <div className="reihe">
          <button
            type="button"
            className="knopf knopf--wichtig"
            aria-pressed={kartenführung === (binFührung ? 'all' : 'leader')}
            onClick={() => {
              /*
               * Zwei Ansichten, eine Fläche — jede Rolle sucht etwas anderes.
               *
               * Die Führung will die ganze Kolonne sehen. Wer hinterherfährt,
               * sucht genau ein Fahrzeug: das vorderste. Bis zum 16.08.2026 bekam
               * er dieselbe eingepasste Kolonnenansicht mit der Spitze irgendwo
               * klein am Rand — die Beschriftung versprach mehr, als dahinterstand.
               *
               * Die Streckenführung dorthin kommt in Stufe 2; bis dahin wird sie
               * nicht vorgetäuscht.
               */
              /*
               * Während einer Navigation ist das ein Blick und kein Zustand —
               * siehe `blick`. Ohne Navigation bleibt die Karte, wo sie
               * hingeschaut hat.
               */
              if (navigation !== null) {
                setBlick(true);
              } else {
                setBlick(false);
                setFolgen(binFührung ? 'all' : 'leader');
              }
              setBlatt('zu');
            }}
          >
            {/*
              Das lange Wort trägt ein weiches Trennzeichen (\u00AD). Es ist
              unsichtbar, solange die Fläche breit genug ist — auf der Tablette
              steht „Führungsfahrzeug zeigen" einzeilig —, und bricht auf dem
              Telefon an genau dieser Stelle um. Der automatischen Silbentrennung
              zu vertrauen hieße, dem Browser die Stelle zu überlassen; Safari
              macht daraus womöglich „Führungsfahr-zeug".
            */}
            {binFührung ? 'Ganze Kolonne zeigen' : 'Führungs\u00ADfahrzeug zeigen'}
          </button>
          {/*
            Ein Zeichen statt des Wortes — damit die Reihe dieselbe Gestalt
            bekommt wie die darunter: eine breite Fläche für den Text, dessen
            Länge sich ändert, und daneben Flächen fester Breite.

            Vorher teilten sich alle drei die Breite zu gleichen Teilen, und
            „Ganze Kolonne zeigen" brach dabei auf dem iPhone in **drei** Zeilen
            um. Der Sinn bleibt über aria-label und title lesbar.
          */}
          {/*
            Die beiden Kartenflächen stehen in der oberen Reihe, seit dem
            28.08.2026. Sie werden während der Fahrt am häufigsten gebraucht und
            bekommen dort die große Tippfläche (92 px statt 64). Meldung und
            Zustandsknopf sind dafür nach unten gewandert.

            Ihre Beschriftung wechselte („Norden oben" / „In Fahrtrichtung") und
            war die längste in der Reihe — dabei ist die Kolonne die einzige,
            deren Text sich ändert und Platz braucht. Der Sinn bleibt über
            aria-label und title lesbar.
          */}
          <button
            type="button"
            className="knopf knopf--symbol"
            title={ausrichtung === 'north' ? 'Norden oben' : 'In Fahrtrichtung'}
            aria-label={
              ausrichtung === 'north'
                ? 'Karte nach Norden ausgerichtet — auf Fahrtrichtung umschalten'
                : 'Karte in Fahrtrichtung — auf Norden umschalten'
            }
            /*
             * Nur die Ausrichtung, nicht die Kartenführung.
             *
             * Bis zum 28.08.2026 setzte diese Fläche zusätzlich die Führung auf
             * „mein Fahrzeug". Damit war die Ausrichtung aus der Kolonnenansicht
             * heraus nicht zu ändern: Jeder Tipp holte die Karte auf einen
             * selbst zurück, und wer die ganze Kolonne sehen wollte, musste
             * anschließend erneut die große Fläche antippen.
             *
             * Das Überbleibsel stammt aus der Zeit, in der die Ausrichtung nur
             * beim Nachführen des eigenen Fahrzeugs galt. Seit `ausrichtungsgrad`
             * die eine Stelle dafür ist, gilt sie in jeder Kartenführung —
             * auch beim Einpassen der Kolonne. Die Fläche hat dort also sehr
             * wohl eine Wirkung; sie brauchte nur nie den Umweg.
             */
            onClick={() => {
              setAusrichtung(ausrichtung === 'north' ? 'course' : 'north');
            }}
          >
            <ZeichenAusrichtung nachNorden={ausrichtung === 'north'} />
          </button>
          {/*
            Das Fadenkreuz zeigt **mich** — für jede Rolle dasselbe.

            Bis zum 16.08.2026 setzte es die Kartenführung auf die Vorgabe der
            eigenen Rolle zurück, für die Führung also auf die ganze Kolonne.
            Damit tat es genau dasselbe wie die große Fläche daneben, und ein
            Führungsfahrzeug hatte überhaupt keinen Weg zurück auf sich selbst.

            Jetzt teilen sich die beiden Flächen die Aufgabe sauber: die große
            zeigt die anderen, das Fadenkreuz einen selbst.

            Es zeigt seinen Zustand als gedrückt und nicht mehr als ausgegraut.
            Ausgegraut heißt „geht gerade nicht" — gemeint war aber „das ist
            gerade an". Am 26.08.2026 hat ein Fahrer genau das nicht erkannt.
            Die Navigationsansicht zählt dazu: Auch sie hält das eigene
            Fahrzeug im Bild, nur weiter unten.
          */}
          <button
            type="button"
            className="knopf knopf--symbol"
            title="Mein Fahrzeug"
            aria-label="Karte auf das eigene Fahrzeug zentrieren"
            aria-pressed={kartenführung === 'self' || kartenführung === 'navigation'}
            onClick={() => {
              /*
               * Beendet auch einen laufenden Blick. Ohne das bliebe die Fläche
               * für dessen Dauer wirkungslos — sie setzt zwar `folgen`, aber der
               * Blick steht in der Rangfolge darüber, und der Fahrer sähe auf
               * seinen Tipp hin nichts geschehen.
               */
              setBlick(false);
              setFolgen('self');
            }}
          >
            <ZeichenZentrieren />
          </button>
        </div>

        <div className="reihe">
          <button
            type="button"
            className="knopf knopf--leise"
            aria-pressed={blatt === 'kolonne'}
            disabled={etwasSteht}
            onClick={() => {
              setBlatt(blatt === 'kolonne' ? 'zu' : 'kolonne');
            }}
          >
            Kolonne ({state.participants.filter((p) => p.connectionState !== 'left').length})
          </button>
          {/*
            Meldung und Zustandsknopf stehen seit dem 28.08.2026 hier unten, in
            der flacheren Reihe. Beide werden seltener gebraucht als die
            Kartenflächen darüber: Eine Meldung setzt ab, wer angehalten hat;
            den Zustandsknopf tippt nur, wer wissen will, was die rote Zahl
            bedeutet — bei Grün ist er ohnehin nicht bedienbar.
          */}
          <button
            type="button"
            className="knopf knopf--leise knopf--symbol"
            title="Meldung"
            aria-label="Meldung an die Kolonne absetzen"
            aria-pressed={blatt === 'meldungen'}
            disabled={etwasSteht}
            onClick={() => {
              setBlatt(blatt === 'meldungen' ? 'zu' : 'meldungen');
            }}
          >
            <ZeichenMeldung />
          </button>
          <Zustandsknopf
            störungen={störungen}
            offen={leisteOffen}
            leise
            onUmschalten={() => {
              const neu = !leisteOffen;
              setLeisteOffenState(neu);
              merkeLeisteOffen(neu);
            }}
          />
        </div>
        </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Ausrichtung der Karte: Kompass oder Fahrtrichtung.
 *
 * Zwei Zustände, die sich schon an der Silhouette unterscheiden müssen — rund
 * gegen spitz. Zwei Pfeile, die beide nach oben zeigen, wären im Vorbeischauen
 * nicht auseinanderzuhalten.
 *
 * Gezeichnet und nicht aus der Schrift geholt: Für beides gibt es kein
 * Zeichen, das jede Systemschrift führt, und ein leeres Kästchen wäre
 * schlimmer als ein blasses.
 */
function ZeichenAusrichtung({ nachNorden }: { nachNorden: boolean }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      {nachNorden ? (
        <>
          {/*
            Nordpfeil mit Buchstaben, wie ihn jede Papierkarte trägt.
            
            Ein Kompassring mit Nadel darin wäre das schönere Bild, säuft aber
            bei dieser Größe ab — die Nadel wird zum Gekritzel. Der Buchstabe
            bleibt lesbar, auch mit siebzig und bei Sonne auf dem Display.
          */}
          <path d="M12 2 L15.5 8 L8.5 8 Z" fill="currentColor" />
          <text
            x="12"
            y="21"
            textAnchor="middle"
            fontSize="16"
            fontWeight="800"
            fill="currentColor"
            fontFamily="system-ui, sans-serif"
          >
            N
          </text>
        </>
      ) : (
        /* Fahrtrichtung: der Zeiger, wie ihn jedes Navigationsgerät benutzt. */
        <path
          d="M12 3 L20 20 L12 15.5 L4 20 Z"
          fill="currentColor"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

/** Zentrieren: das Fadenkreuz, das auf jeder Karte dasselbe bedeutet. */
function ZeichenZentrieren(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="32"
      height="32"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <circle cx="12" cy="12" r="6.5" fill="none" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="12" r="2.2" fill="currentColor" />
      <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
        <line x1="12" y1="1.5" x2="12" y2="4.5" />
        <line x1="12" y1="19.5" x2="12" y2="22.5" />
        <line x1="1.5" y1="12" x2="4.5" y2="12" />
        <line x1="19.5" y1="12" x2="22.5" y2="12" />
      </g>
    </svg>
  );
}

/**
 * Meldung: die Sprechblase.
 *
 * Zwei Zeichen waren schon vergeben, und beide schieden deshalb aus. Ein
 * **Lautsprecher** trägt die Sprachansage (lib/manoeverzeichen.tsx) — der sagt,
 * ob die App spricht, nicht dass man etwas mitteilt. Ein **Ausrufezeichen**
 * trägt die Meldung „Gefahrenstelle" (lib/meldezeichen.tsx), und die steht als
 * eine der Möglichkeiten in genau dem Blatt, das dieser Knopf öffnet; der Knopf
 * sähe damit aus, als melde er speziell eine Gefahr.
 *
 * Bleibt die Blase mit zwei Textzeilen: eine Nachricht, ohne etwas über ihren
 * Inhalt zu behaupten. Von hier aus geht auch „Alles in Ordnung" hinaus.
 *
 * Die zwei Zeilen sind kein Schmuck. Eine leere Blase liest bei vierunddreißig
 * Pixeln als Kästchen mit Zipfel; gefüllt trägt sie auch aus der Halterung
 * heraus.
 */
function ZeichenMeldung(): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="34"
      height="34"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block' }}
    >
      <path
        d="M3.5 5 H20.5 V16.5 H12.5 L8 20.8 V16.5 H3.5 Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.3"
        strokeLinejoin="round"
      />
      <g stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
        <line x1="7.5" y1="9.4" x2="16.5" y2="9.4" />
        <line x1="7.5" y1="12.6" x2="13.5" y2="12.6" />
      </g>
    </svg>
  );
}

/**
 * Das Blatt, das von unten aufklappt.
 *
 * Oben hat es eine feste Kante — dort beginnt es, gegen die Karte. Unten
 * bekommt es keine: Dort **endet** es nicht, dort geht es weiter. Eine harte
 * Linie behauptete das Gegenteil und schnitte die letzte Fläche mittendurch ab.
 *
 * Stattdessen läuft der Inhalt weich aus — aber nur, solange darunter wirklich
 * noch etwas liegt. Passt alles hinein, bleibt die letzte Fläche unangetastet;
 * ein Auslaufen ohne Grund wäre eine Behauptung über Inhalt, den es nicht gibt.
 */
/**
 * Der Pfeil, der die Möglichkeiten eines Eintrags auf- und zuklappt.
 *
 * Gezeichnet wie die übrigen Zeichen — gleiche Fläche, gleiche Strichstärke.
 * Offen zeigt er nach oben: Das ist die Richtung, in die der Block wieder
 * verschwindet.
 */
function ZeichenAufklappen({ offen }: { offen: boolean }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      width="30"
      height="30"
      aria-hidden="true"
      focusable="false"
      style={{ display: 'block', transform: offen ? 'rotate(180deg)' : undefined }}
    >
      <path
        d="M6 9.5 12 15.5 18 9.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Blatt({ children }: { children: React.ReactNode }): React.JSX.Element {
  const kasten = useRef<HTMLDivElement | null>(null);
  const [gehtWeiter, setGehtWeiter] = useState(false);

  // Ohne Abhängigkeitsliste: Der Inhalt ändert sich, ohne dass sich die Größe
  // des Kastens ändert — eine eingehende Meldung etwa. Nach jedem Zeichnen neu
  // zu messen ist billiger als ein Beobachter für beide Fälle.
  useEffect(() => {
    const el = kasten.current;
    if (el === null) {
      return;
    }
    const messen = (): void => {
      setGehtWeiter(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
    };
    messen();
    el.addEventListener('scroll', messen, { passive: true });
    return () => {
      el.removeEventListener('scroll', messen);
    };
  });

  return (
    <div className="blatt" data-geht-weiter={gehtWeiter ? 'ja' : 'nein'} ref={kasten}>
      {children}
    </div>
  );
}

function istDunkel(): boolean {
  const gewählt = currentTheme();
  if (gewählt === 'dunkel') {
    return true;
  }
  if (gewählt === 'hell') {
    return false;
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Der Abbiegehinweis über der Karte — Stufe 2.
 *
 * Alles, was ein Fahrer im Auto in einer Sekunde erfassen muss, und nichts
 * darüber hinaus: Zeichen, Entfernung, Hinweis. Darunter, kleiner, wie weit es
 * noch bis zur Kolonne ist.
 *
 * Nichts hier wird angetippt (`pointer-events: none` in der CSS): Der Block
 * sitzt oben, wo der Daumen nicht hinreicht, und alles Bedienbare — beenden,
 * Ansage ein und aus — steht unten bei den übrigen Flächen.
 *
 * Führt die Route ausnahmsweise **nicht** entlang der gefahrenen Spur, steht
 * das dabei. Eine Route, die aussieht wie die Strecke der Ausfahrt und es nicht
 * ist, wäre schlimmer als gar keine.
 */
function Navigationsbalken({
  route,
  fortschritt,
  messung,
  gemessenAm,
}: {
  route: NavigationRoute;
  fortschritt: RouteProgress | null;
  messung: PositionSample | null;
  gemessenAm: number | null;
}): React.JSX.Element | null {
  if (fortschritt === null) {
    return (
      <div className="navibalken" role="status">
        <p className="navibalken__warten">
          Die Navigation steht bereit. Sobald der eigene Standort da ist, geht es los.
        </p>
      </div>
    );
  }

  const manöver: Maneuver | undefined = route.maneuvers[fortschritt.maneuverIndex];
  if (manöver === undefined) {
    return null;
  }

  return (
    <div className="navibalken" role="status">
      <div className="navibalken__kopf">
        <span className="navibalken__zeichen">
          <ZeichenManöver kind={manöver.kind} />
        </span>
        <div className="navibalken__wort">
          {/*
            Auch beim letzten Eintrag. Ohne die Entfernung stand dort ein Satz
            ohne Maß — und weil er bis zum 20.08.2026 „Du bist wieder bei der
            Kolonne" hieß, las er sich wie eine Ankunft, während daneben „noch
            1,1 km" stand.
          */}
          <span className="navibalken__entfernung">
            {/*
              „Jetzt" statt „0 m".
              
              Ein fälliges Wenden hat keine Entfernung — es gilt hier, an dieser
              Stelle. „0 m" wäre zwar richtig gerechnet, läse sich am Steuer
              aber wie ein Fehler. Alles andere behält sein Maß.
            */}
            {fortschritt.distanceToManeuverM < 5
              ? 'Jetzt'
              : shortDistanceM(fortschritt.distanceToManeuverM)}
          </span>
          <span className="navibalken__hinweis">{manöver.instruction}</span>
        </div>
      </div>

      <p className="navibalken__fuss">
        <span>
          {/* Restzeit als MM:SS wie der Rückstand — sie zählt genauso im
              Sekundentakt herunter und darf dabei nicht die Breite wechseln. */}
          noch {shortDistanceM(fortschritt.remainingM)} ·{' '}
          {formatMinutesSeconds(fortschritt.remainingMs / 1000)}
        </span>
        {!route.alongTrack && (
          <span className="navibalken__abweichung">nicht die gefahrene Strecke</span>
        )}
        {/*
          Tempo und Fahrtrichtung wandern hierher, solange eine Navigation
          läuft — der eigene Kasten oben rechts entfällt dann.

          Warum in die Fußzeile und nicht neben den Hinweis: Dort nähme das
          Tempo dem Hinweis die Breite, und lange Anweisungen wie „Auf
          Verkeersplein Westerweg fahren und die 2. Ausfahrt nehmen" brauchen
          eine Zeile mehr. Gemessen bei 375 px: neben dem Hinweis wächst der
          Kasten auf 182 Pixel, in der Fußzeile bleibt er bei 160 — und die
          ganze Überlagerung spart gegenüber zwei Kästen 51 Pixel.
        */}
        <Fahrtwerk messung={messung} gemessenAm={gemessenAm} />
      </p>
    </div>
  );
}

/**
 * Tempo und Fahrtrichtung des eigenen Fahrzeugs.
 *
 * Aus der **eigenen** Messung, nicht aus der Sammelmeldung des Servers: Sie ist
 * die frischeste Quelle, und die Anzeige läuft damit auch weiter, wenn die
 * Verbindung abgerissen ist und von der Kolonne nichts mehr zu sehen ist.
 *
 * Drei Regeln stecken darin, und alle drei stammen aus dem Leitfaden:
 *
 *   * **Richtung nie nur als Nadel.** Eine Nadel allein zwingt zum genauen
 *     Hinsehen; das Kürzel liest man im Vorbeischauen. Zeichen *und* Wort.
 *   * **Unter Schrittgeschwindigkeit keine Nadel.** Der GPS-Kurs ist dann
 *     Rauschen — die Nadel drehte sich an der Ampel im Kreis und behauptete
 *     dabei, das sei die Fahrtrichtung. Die Schwelle ist dieselbe, an der auch
 *     der Spurabgleich dem Kurs nicht mehr traut.
 *   * **Nichts Veraltetes.** Bleibt die Messung aus — Gerät im Hintergrund,
 *     kein Empfang —, steht dort ein Strich und kein eingefrorener Wert. Es ist
 *     dieselbe Regel wie bei den Fahrzeugen in der Kolonne.
 *
 * Am Rechner liefert der Browser weder `speed` noch `heading`; dort bleibt der
 * Block auf „kein Signal". Gerechnet wird daraus nichts — ein erfundener Wert
 * wäre schlimmer als keiner.
 */
/**
 * Tempo und Fahrtrichtung in einer Zeile — die schmale Fassung für die Fußzeile
 * des Abbiegehinweises. Dieselben Regeln wie im eigenen Kasten, nur in einer
 * Zeile statt in zweien.
 */
function Fahrtwerk({
  messung,
  gemessenAm,
}: {
  messung: PositionSample | null;
  gemessenAm: number | null;
}): React.JSX.Element {
  const { tempo, richtung, kursGrad, wort } = berechneFahrtwerte(messung, gemessenAm, Date.now());

  return (
    <span
      className="navibalken__tempo"
      aria-label={
        tempo === null
          ? 'Tempo und Fahrtrichtung liegen nicht vor'
          : `Tempo ${String(tempo)} Kilometer je Stunde` +
            (richtung === null ? '' : `, Fahrtrichtung ${COMPASS_NAMES[richtung]}`)
      }
    >
      {tempo === null ? '—' : tempo} km/h ·{' '}
      {richtung !== null && kursGrad !== undefined && <ZeichenKurs grad={kursGrad} />}
      {wort}
    </span>
  );
}

/**
 * Der nächste Treffpunkt und wie weit es noch ist.
 *
 * ## Warum „noch 12 min" und keine Uhrzeit
 *
 * Die Zahl stammt aus `travelMs` der Führungsspur — der **tatsächlich
 * gefahrenen** Zeit dieser Kolonne. Die wächst im Stillstand nicht, sonst
 * zählte jede Ampel als Rückstand. Für eine Ankunftszeit heißt das: Es ist
 * Fahrzeit, keine Uhrzeit, und an einer Strecke durch zwölf Ortsdurchfahrten
 * fällt sie zu günstig aus.
 *
 * Eine Uhrzeit hinzuschreiben hieße, diese Unschärfe zu verbergen. „Noch
 * 12 min" sagt, was gemessen ist — und wer daraus eine Uhrzeit machen will,
 * kann das selbst (A-119).
 *
 * ## Warum die Entfernung dabeisteht
 *
 * Sie ist gemessen, die Zeit ist gerechnet. Fehlt das Tempo — am Anfang einer
 * Ausfahrt, solange die Führung kaum gefahren ist —, bleibt die Entfernung und
 * die Zeit fehlt. Eine halbe Auskunft ist besser als eine erfundene.
 */
/**
 * Wer ist schon da — am Treffpunkt.
 *
 * ## Warum Kennzeichen und nicht Namen
 *
 * „Das ist das Einzige, woran ein Fahrzeug in der Kolonne zweifelsfrei zu
 * erkennen ist: Namen wiederholen sich" — dieselbe Begründung, aus der das
 * Kennzeichen beim Beitritt Pflicht ist. Wer am Sammelpunkt nachzählt, sieht
 * Autos und keine Vornamen.
 *
 * ## Warum die Fehlenden und nicht die Anwesenden
 *
 * Die Frage lautet „wer fehlt noch?". Die Anwesenden stehen daneben und müssen
 * nicht aufgezählt werden; ihre **Zahl** genügt, damit man sich nicht
 * verzählt. Sind alle da, steht genau das da — der Satz, auf den gewartet wird.
 */
function Ankunftsbalken({
  waypoint,
  anwesend,
  teilnehmer,
}: {
  waypoint: Waypoint;
  anwesend: Anwesenheit;
  teilnehmer: readonly Participant[];
}): React.JSX.Element {
  const gesamt = anwesend.da.length + anwesend.fehlen.length;
  const kennzeichen = (id: string): string =>
    teilnehmer.find((t) => t.id === id)?.licensePlate ?? '—';

  /*
   * Höchstens vier, dann eine Zahl. Zwölf Kennzeichen untereinander schieben
   * die Karte aus dem Bild, und wer zwölf vermisst, zählt nicht mehr nach.
   */
  const zeigen = anwesend.fehlen.slice(0, LAYOUT.fehlendeSichtbar);
  const weitere = anwesend.fehlen.length - zeigen.length;

  return (
    <div
      className="ankunftsbalken"
      aria-label={
        `${WAYPOINT_KIND_NAMES[waypoint.kind]}: ${waypoint.name}. ` +
        `${anwesend.da.length} von ${gesamt} sind da` +
        (anwesend.fehlen.length === 0
          ? ', alle vollzählig'
          : `. Es fehlen: ${anwesend.fehlen.map(kennzeichen).join(', ')}`)
      }
    >
      <span className="ankunftsbalken__name">{waypoint.name}</span>
      <span className="ankunftsbalken__zahl">
        {anwesend.da.length} von {gesamt} da
      </span>
      {anwesend.fehlen.length === 0 ? (
        /* Nicht allein über die Farbe — der Satz sagt es. */
        <span className="ankunftsbalken__vollzaehlig">✓ Alle da</span>
      ) : (
        <span className="ankunftsbalken__fehlen">
          <span className="ankunftsbalken__wort">fehlt noch</span>
          {zeigen.map((id) => (
            <span key={id} className="ankunftsbalken__kennzeichen">
              {kennzeichen(id)}
            </span>
          ))}
          {weitere > 0 && <span className="ankunftsbalken__wort">und {weitere} weitere</span>}
        </span>
      )}
    </div>
  );
}

function Treffpunktbalken({
  schaetzung,
}: {
  schaetzung: ArrivalEstimate;
}): React.JSX.Element {
  const { waypoint, remainingM, travelS, entlangDerRoute } = schaetzung;
  const zeit = travelS === null ? null : formatDuration(travelS * 1000);
  /*
   * „noch" heißt: entlang der Strecke, also der Weg, den die Kolonne fährt.
   * „Luftlinie" heißt: Du bist nicht auf der Route, und wie weit es auf der
   * Straße ist, weiß niemand. Der Unterschied gehört auf den Bildschirm —
   * am 16.09.2026 stand „noch 21 m" bei 356 m Entfernung (A-125).
   */
  const wort = entlangDerRoute ? 'noch' : 'Luftlinie';

  return (
    <div
      className="treffpunktbalken"
      aria-label={
        `Nächster ${WAYPOINT_KIND_NAMES[waypoint.kind]}: ${waypoint.name}, ` +
        (entlangDerRoute
          ? `noch ${formatDistance(remainingM)} entlang der Strecke`
          : `${formatDistance(remainingM)} Luftlinie, nicht auf der Strecke`) +
        (zeit === null ? ', Fahrzeit noch unbekannt' : `, etwa ${zeit} Fahrzeit`)
      }
    >
      {/*
        Der Name zuerst und über die ganze Breite.
        Auf 390 Bildpunkten stand am 15.09.2026 „Sammeln am O…" da — die Art
        und die Entfernung hatten den Platz genommen. Der Name ist aber das
        Einzige, woran ein Fahrer den Ort wiedererkennt; Art und Entfernung
        stehen darunter und bleiben trotzdem lesbar.
      */}
      <span className="treffpunktbalken__name">{waypoint.name}</span>
      <span className="treffpunktbalken__zeile2">
        {/* Die Art als Wort und nicht als Farbe — Abschnitt 2. */}
        <span className="treffpunktbalken__art">{WAYPOINT_KIND_NAMES[waypoint.kind]}</span>
        <span className="treffpunktbalken__rest">
          {wort} {formatDistance(remainingM)}
          {zeit !== null && <span className="treffpunktbalken__zeit">{` · ${zeit}`}</span>}
        </span>
      </span>
    </div>
  );
}

function Fahrtwerte({
  messung,
  gemessenAm,
}: {
  messung: PositionSample | null;
  gemessenAm: number | null;
}): React.JSX.Element {
  const { tempo, richtung, kursGrad, wort: zweiteZeile } = berechneFahrtwerte(messung, gemessenAm, Date.now());

  return (
    <div
      className="fahrtwerte"
      aria-label={
        tempo === null
          ? 'Tempo und Fahrtrichtung liegen nicht vor'
          : `Tempo ${tempo} Kilometer je Stunde` +
            (richtung === null ? '' : `, Fahrtrichtung ${COMPASS_NAMES[richtung]}`)
      }
    >
      <div>
        <span className="fahrtwerte__zahl">{tempo === null ? '—' : tempo}</span>
        <span className="fahrtwerte__einheit">km/h</span>
      </div>
      <div className={`fahrtwerte__kurs ${richtung === null ? 'fahrtwerte__kurs--ruhig' : ''}`}>
        {richtung !== null && kursGrad !== undefined && <ZeichenKurs grad={kursGrad} />}
        <span>{zweiteZeile}</span>
      </div>
    </div>
  );
}

/** Die Nadel. Zeigt in die Fahrtrichtung, bezogen auf Norden — wie die Karte in ihrer Vorgabe. */
function ZeichenKurs({ grad }: { grad: number }): React.JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ transform: `rotate(${grad}deg)`, display: 'block' }}
    >
      <path d="M12 2.5 L18.2 20.5 L12 16.2 L5.8 20.5 Z" fill="currentColor" />
    </svg>
  );
}

/** Was die Zustandsleiste über die Verbindung und das Gerät weiß. */
interface Gerätezustand {
  readonly verbindung: string;
  readonly wachhalten: string;
  readonly wachhaltenGrund: string | null;
  readonly gepuffert: number;
  readonly gepufferteMeldungen: number;
  readonly pausiert: boolean;
  readonly unsichtbar: boolean;
  /** Nur im Prüfmodus gesetzt — sonst `null` und damit nirgends gezählt (A-139). */
  readonly geraetOfflineSeit: number | null;
}

/**
 * Wie viele Merkmale gerade **nicht** in Ordnung sind.
 *
 * Grundlage für zweierlei: die Farbe des Zustandsknopfes und die Zahl darauf.
 * Beide müssen dasselbe zählen wie die Leiste zeigt — deshalb steht die Regel
 * hier und nicht zweimal.
 */
function zähleStörungen(zustand: Gerätezustand): number {
  return (
    (zustand.unsichtbar ? 1 : 0) +
    (zustand.verbindung === 'open' ? 0 : 1) +
    (zustand.pausiert ? 1 : 0) +
    (zustand.wachhalten === 'active' ? 0 : 1) +
    (zustand.gepuffert > 0 ? 1 : 0) +
    (zustand.gepufferteMeldungen > 0 ? 1 : 0) +
    (zustand.geraetOfflineSeit === null ? 0 : 1)
  );
}

/**
 * Der Knopf, der die Zustandsleiste auf- und zuklappt — und dabei selbst sagt,
 * ob etwas nicht stimmt.
 *
 * Grün mit einem Punkt heißt: alles in Ordnung. Rot mit einer Zahl heißt: so
 * viele Merkmale stehen in der Leiste. Die Zahl ist nicht Zierrat, sondern die
 * Vorschrift aus Abschnitt 2 — Farbe allein trägt keinen Zustand, bei Sonne
 * geht sie als Erstes verloren.
 *
 * Darunter steht, was ein Tipp bewirkt. Ohne das wüsste niemand, ob er die
 * Leiste holt oder wegnimmt.
 */
function Zustandsknopf({
  störungen,
  offen,
  leise,
  onUmschalten,
}: {
  störungen: number;
  offen: boolean;
  /** In der flacheren der beiden Reihen — dieselbe Höhe wie ihre Nachbarn. */
  leise: boolean;
  onUmschalten: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      className={
        `knopf${leise ? ' knopf--leise' : ''} knopf--symbol knopf--zustand` +
        (störungen > 0 ? ' knopf--zustand-stoerung' : '')
      }
      aria-label={
        störungen === 0
          ? 'Alles in Ordnung'
          : `${String(störungen)} Hinweise zum Gerät. ` +
            (offen ? 'Leiste zuklappen' : 'Leiste aufklappen')
      }
      /*
       * Gibt es nichts zu zeigen, gibt es nichts aufzuklappen. Grün mit Punkt
       * ist dann eine Auskunft und keine Fläche — ein Tipp, der nichts bewirkt,
       * lässt den Fahrer zweifeln, ob er getroffen hat.
       */
      disabled={störungen === 0}
      onClick={onUmschalten}
    >
      <span className="zustandknopf">
        <span className="zustandknopf__zahl" aria-hidden="true">
          {störungen === 0 ? '●' : störungen}
        </span>
        {störungen > 0 && (
          <span className="zustandknopf__wort" aria-hidden="true">
            {offen ? 'ZU' : 'AUF'}
          </span>
        )}
      </span>
    </button>
  );
}

function Zustandsleiste({
  verbindung,
  wachhalten,
  wachhaltenGrund,
  gepuffert,
  gepufferteMeldungen,
  pausiert,
  unsichtbar,
  geraetOfflineSeit,
}: {
  verbindung: string;
  wachhalten: string;
  /**
   * Woran die Bildschirmsperre scheiterte — als Titel am Merkmal, für den, der
   * hinterher nachsieht. Auf dem Bildschirm während der Fahrt steht der Satz,
   * nicht der Fehlername.
   */
  wachhaltenGrund: string | null;
  gepuffert: number;
  gepufferteMeldungen: number;
  pausiert: boolean;
  unsichtbar: boolean;
  /** Seit wann das Gerät „offline" meldet — nur im Prüfmodus gesetzt (A-139). */
  geraetOfflineSeit: number | null;
}): React.JSX.Element | null {
  /*
   * Ohne Abweichung kein Kasten. Ein leerer Streifen kostet Polsterung und
   * Höhe, ohne etwas zu sagen — und was zu sagen wäre, sagt der Knopf.
   */
  if (
    !unsichtbar &&
    verbindung === 'open' &&
    !pausiert &&
    wachhalten === 'active' &&
    gepuffert === 0 &&
    gepufferteMeldungen === 0 &&
    geraetOfflineSeit === null
  ) {
    return null;
  }

  return (
    <div className="leiste">
      {/* Wer sich unsichtbar geschaltet hat, muss das dauerhaft sehen. Sonst
          wundert er sich auf halber Strecke, warum ihn niemand sucht. */}
      {unsichtbar && (
        <span className="merkmal merkmal--achtung">
          <ZeichenUnsichtbar />Unsichtbar
        </span>
      )}
      {/*
        Steht die Verbindung, steht hier gar nichts.

        Das „alles in Ordnung" trägt der Zustandsknopf mit seinem grünen Grund
        und dem Punkt darauf — es zweimal zu sagen kostet nur Platz. Die Leiste
        zeigt deshalb ausschließlich Abweichungen, und gibt es keine, entsteht
        sie gar nicht erst.
      */}
      {verbindung !== 'open' && (
        <span className="merkmal merkmal--achtung">
          <span aria-hidden="true">↻</span>Keine Verbindung
        </span>
      )}

      {pausiert && (
        <span className="merkmal merkmal--ruhend">
          <span aria-hidden="true">❚❚</span>Im Hintergrund — sendet nicht
        </span>
      )}

      {/*
        Nur im Prüfmodus (A-139): was der Browser über das Netz des Geräts
        sagt. Nicht dasselbe wie „Keine Verbindung" darüber — das schließt die
        App aus ihrem Herzschlag, und bis dahin kann eine Minute vergehen. Steht
        dies hier, während die Verbindung noch als offen gilt, ist genau das die
        Auskunft, um die es geht.
      */}
      {geraetOfflineSeit !== null && (
        <span className="merkmal merkmal--ruhend">
          <span aria-hidden="true">⊘</span>Gerät meldet offline · seit{' '}
          {new Date(geraetOfflineSeit).toLocaleTimeString('de-DE')}
        </span>
      )}

      {/*
        Drei Fälle, drei Sätze — und das ist keine Wortklauberei.

        „Nicht möglich" heißt: Dieser Browser kennt die Bildschirmsperre nicht,
        daran ist nichts zu machen. „Abgelehnt" heißt: Er kennt sie, gibt sie
        aber nicht heraus — und der bei weitem häufigste Grund dafür ist der
        Stromsparmodus, den iOS unter 20 % Akku von selbst einschaltet und bis
        80 % anlässt. Das ist mit einem Griff behoben, wenn man es weiß.

        Bis zum 18.08.2026 stand für beides derselbe Satz da. Wer ihn las,
        konnte nicht wissen, ob er etwas tun kann.
      */}
      {wachhalten !== 'active' && (
        <span className="merkmal merkmal--achtung" title={wachhaltenGrund ?? undefined}>
          <span aria-hidden="true">☾</span>
          {wachhalten === 'unsupported'
            ? 'Bildschirmsperre nicht möglich'
            : wachhalten === 'denied'
              ? 'Bildschirm kann ausgehen — Stromsparmodus?'
              : 'Bildschirm kann ausgehen'}
        </span>
      )}

      {gepuffert > 0 && (
        <span className="merkmal merkmal--achtung">
          <span aria-hidden="true">⇪</span>
          {gepuffert} Positionen warten
        </span>
      )}

      {/*
        Getrennt von den Positionen, und mit eigenen Worten.

        Eine wartende Position ist ein Loch in der Spur. Eine wartende Panne ist
        Hilfe, die noch niemand angefordert hat — wer darauf getippt hat, muss
        wissen, dass sie noch nicht draußen ist (A-111). Bis zum 14.09.2026
        verschwand sie lautlos.
      */}
      {gepufferteMeldungen > 0 && (
        <span className="merkmal merkmal--achtung">
          <span aria-hidden="true">✉</span>
          {gepufferteMeldungen === 1
            ? 'Deine Meldung ist noch nicht raus'
            : `${gepufferteMeldungen} Meldungen sind noch nicht raus`}
        </span>
      )}
    </div>
  );
}

/**
 * Die Form, die eine Rolle im Kolonnenzeichen trägt.
 *
 * Führung und Schlusswagen haben eine feste Farbe — aber Farbe allein darf nie
 * eine Rolle kennzeichnen. Deshalb trägt jede der beiden zusätzlich eine eigene
 * Form: die Führung eine Raute, der Schlusswagen ein nach unten gerundetes
 * Wappen. Ein Quadrat wäre dafür untauglich, das ist eine gedrehte Raute und im
 * Augenwinkel nicht sicher davon zu unterscheiden.
 */
function rollenform(role: ParticipantRole): string {
  switch (role) {
    case 'leader':
      return 'punkt--führung';
    case 'sweeper':
      return 'punkt--schluss';
    case 'participant':
      return '';
  }
}

function Kolonne({
  participants,
  vehicles,
  lastReportAt,
  youId,
  binFührung,
  gelesenVon,
  ansageAb,
  onEntfernen,
  onFührungÜbergeben,
}: {
  participants: readonly Participant[];
  vehicles: readonly VehicleState[];
  /** Wann zuletzt eine neue Meldung eintraf, je Teilnehmer — eigene Uhr. */
  lastReportAt: ReadonlyMap<string, number>;
  youId: string | null;
  binFührung: boolean;
  onEntfernen: (participantId: string) => void;
  /**
   * Wer die jüngste eigene Ansage gelesen hat — oder null, wenn keine offen
   * ist. Nur die Führung bekommt das zu sehen.
   */
  gelesenVon: readonly string[] | null;
  /** Wann die Ansage abgesetzt wurde — wer später kam, wird nicht gefragt. */
  ansageAb: number | null;
  onFührungÜbergeben: (participantId: string) => void;
}): React.JSX.Element {
  const jetzt = Date.now();

  /**
   * Welcher Eintrag zeigt gerade seine Schaltflächen?
   *
   * „Führung übergeben" und „Aus der Kolonne nehmen" sind wichtig und selten.
   * Ständig ausgeklappt nahmen sie in jedem Eintrag zwei Zeilen — auf dem
   * iPhone blieb für die Kolonne kaum noch Platz. Eingeklappt genügt ein Pfeil.
   *
   * Immer nur einer offen: Zwei aufgeklappte Einträge schieben die Liste so
   * weit, dass man den aus dem Blick verliert, um den es gerade ging.
   */
  const [offenFür, setOffenFür] = useState<string | null>(null);

  const sortiert = [...participants]
    .filter((p) => p.connectionState !== 'left')
    .sort((a, b) => {
      if (a.role === 'leader') return -1;
      if (b.role === 'leader') return 1;
      const va = vehicles.find((f) => f.participantId === a.id)?.delaySeconds ?? Number.MAX_VALUE;
      const vb = vehicles.find((f) => f.participantId === b.id)?.delaySeconds ?? Number.MAX_VALUE;
      return va - vb;
    });

  return (
    <ul className="liste">
      {sortiert.map((teilnehmer) => {
        const fahrzeug = vehicles.find((f) => f.participantId === teilnehmer.id) ?? null;
        /*
         * Wie alt ist das, was hier steht?
         *
         * Gemessen daran, wann zuletzt eine **neue** Meldung dieses Fahrzeugs
         * eintraf — nach der eigenen Uhr. Nicht an seinem `recordedAt`: Das
         * ist die Uhr des fremden Geräts, und geht die falsch, ist die Angabe
         * falsch. In den Daten steht dafür ein Beleg — eine Position mit
         * Zeitstempel aus dem Jahr 1995.
         */
        const gemeldet = fahrzeug === null ? undefined : lastReportAt.get(teilnehmer.id);
        const alter = gemeldet === undefined ? null : jetzt - gemeldet;
        // Unbekannt gilt als veraltet — die Begründung steht in `istVeraltet`.
        const veraltet = istVeraltet(alter);

        /*
         * Was der Pfeil aufklappt — und ob es ihn überhaupt gibt.
         *
         * Bis zum 02.09.2026 hing der Pfeil an der **Rolle**: Nur die Führung
         * hatte ihn, weil darunter „Führung übergeben" und „Aus der Kolonne
         * nehmen" lagen. Als der erklärende Satz zum Zustand dazukam (A-89),
         * erbte er diese Bedingung — und damit sah ihn ausgerechnet der
         * Schlusswagen nicht, der die Lage genauso beurteilen muss.
         *
         * Jetzt hängt der Pfeil am **Inhalt**: Er erscheint, wenn es etwas
         * aufzuklappen gibt, gleich für wen. Wer welchen Teil zu sehen
         * bekommt, entscheidet weiterhin die Rolle — aber je Teil, nicht für
         * das Ganze. Kommt später etwas hinzu, das auch ein Kolonnenfahrzeug
         * braucht, ist der Platz dafür da und die Bedingung stimmt schon.
         */
        const zustandssatz =
          teilnehmer.visible && fahrzeug !== null ? CONVOY_LABELS[fahrzeug.convoyState] : null;
        const führungsflächen = binFührung && teilnehmer.id !== youId;
        const gibtEtwasAufzuklappen = zustandssatz !== null || führungsflächen;
        const offen = offenFür === teilnehmer.id;

        return (
          <li
            key={teilnehmer.id}
            className={[
              'eintrag',
              teilnehmer.id === youId ? 'eintrag--ich' : '',
              veraltet ? 'eintrag--veraltet' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span
              className={`punkt ${rollenform(teilnehmer.role)}`}
              style={{ background: displayColor(teilnehmer.role, teilnehmer.color) }}
              aria-hidden="true"
            >
              <span>{initials(teilnehmer.displayName)}</span>
            </span>

            <div className="eintrag__text">
              {/*
                Die Namenszeile gehört dem Namen.

                Vorher hingen „(du)", „· Führung" und „· Schluss" hier hinten
                dran — in einer Zeile, die auf eine Zeile beschnitten wird.
                Damit verlor ausgerechnet der Name gegen ein Wort, das links
                schon als Form und Farbe steht: Auf dem iPhone stand „iPad ·
                Sc…". Der Name ist das, woran man ein Fahrzeug erkennt.
              */}
              <div className="eintrag__name">{teilnehmer.displayName}</div>

              {/* Das Kennzeichen steht für sich, nicht hinter dem Fahrzeug:
                  Danach sucht man, wenn man wissen will, wer da vor einem
                  fährt. Leer ist es nur bei Teilnehmern aus Ausfahrten von vor
                  der Kennzeichenpflicht.

                  Daneben die Merkzeichen: Rolle und „Du". Sie stehen als Wort
                  da, nicht nur als Form — eine Raute sagt niemandem „Führung",
                  solange es ihm keiner erklärt hat, und ein Teil der Fahrer
                  ist über siebzig. Die Farbe ist dieselbe wie beim Zeichen
                  links; beide sagen dasselbe. */}
              <div className="eintrag__kennzeichen">
                {teilnehmer.licensePlate !== '' && <span>{teilnehmer.licensePlate}</span>}
                {teilnehmer.id === youId && <span className="rolle rolle--ich">Du</span>}
                {teilnehmer.role === 'leader' && (
                  <span className="rolle rolle--fuehrung">Führung</span>
                )}
                {teilnehmer.role === 'sweeper' && (
                  <span className="rolle rolle--schluss">Schluss</span>
                )}
              </div>

              <div className="eintrag__unten">{fahrzeugText(teilnehmer)}</div>

              {/*
                Das Alter der Anzeige — eine eigene Zeile.

                Vorher hing es mit einem Punkt hinter dem Fahrzeug („iPad (2000)
                · Position 2 min alt"). Damit las es sich wie eine weitere
                Angabe zum Fahrzeug, und auf dem iPhone brach es mitten im Satz
                um: „… · Position 2 min" / „alt". Es ist aber eine Aussage über
                die **Anzeige**, nicht über das Auto — und die gehört auf eine
                eigene Zeile.

                Nicht „Stand vor 18 s": „Steht" ist in dieser Anwendung ein
                Kolonnenzustand, und der Satz läse sich wie eine Aussage über
                das Fahrverhalten — als sei das Fahrzeug vor 18 Sekunden stehen
                geblieben. „Meldung" scheidet ebenso aus: Das Wort ist hier für
                die Statusmeldungen vergeben. Bleibt die schlichte Aussage —
                was du siehst, ist so alt.
              */}
              {veraltet && alter !== null && (
                <div className="eintrag__alter">Position {formatDuration(alter)} alt</div>
              )}

              {/*
                Hat er die Ansage der Führung gelesen?

                Eigene Zeile mit eigener Umrandung — vorher hing das Wort hinten
                an der Fahrzeugangabe (`iPad / 2000 · gelesen`), in derselben
                gedämpften Schrift und nur durch einen Punkt getrennt. Das las
                sich wie eine Angabe zum Fahrzeug und brach auf dem iPhone um.

                Und beide Fälle waren gleich laut. Die Führung sucht hier aber
                nur einen: wer es **noch nicht** gelesen hat. Deshalb bekommt
                das Offene die Achtungsfarbe und eine wartende Uhr, das
                Erledigte das ruhige Grün — Zeichen und Wort, wie überall, denn
                bei Sonne geht die Farbe als Erstes verloren.
              */}
              {gelesenVon !== null &&
                teilnehmer.id !== youId &&
                ansageAb !== null &&
                presentWhenSent(teilnehmer.joinedAt, ansageAb) && (
                <span
                  className={`quittung ${
                    gelesenVon.includes(teilnehmer.id) ? 'quittung--gelesen' : 'quittung--offen'
                  }`}
                >
                  {gelesenVon.includes(teilnehmer.id) ? (
                    <>
                      <ZeichenGelesen />
                      Gelesen
                    </>
                  ) : (
                    <>
                      <ZeichenOffen />
                      Ungelesen
                    </>
                  )}
                </span>
              )}
            </div>

            <div className="eintrag__rechts">
              {/*
                Ein unsichtbar geschaltetes Fahrzeug hat hier keinen Zustand —
                aber nicht, weil sein Gerät aufhörte zu messen. Es misst und
                sendet weiter; der Server nimmt die Positionen an und gibt die
                Auswertung nur nicht mehr weiter (hub.ts: „Wer sich unsichtbar
                geschaltet hat, wird nicht weitergegeben"). Genau das sagt der
                Auftrag zu, Abschnitt 11: Die Position wird nicht mehr
                **weitergegeben**. Wer gar nichts mehr senden will, verlässt die
                Ausfahrt.

                Ohne diese Kennzeichnung stünde das Fahrzeug ohne Zustand in der
                Liste, und die Führung wüsste nicht, ob es stumm ist oder ob
                etwas kaputt ist.

                Die frühere Fassung dieses Kommentars behauptete, es sende nicht
                mehr. Die Schlussfolgerung stimmte, die Begründung nicht — und
                eine falsche Begründung im Code ist die Sorte Satz, auf die sich
                später jemand beruft.
              */}
              {!teilnehmer.visible ? (
                <span className="merkmal merkmal--ruhend">
                  <ZeichenUnsichtbar />Unsichtbar
                </span>
              ) : (
                // Auf fahrzeug geprüft, nicht auf label: Beides hängt zusammen,
                // aber nur die Prüfung auf fahrzeug erspart ein Ausrufezeichen,
                // mit dem man dem Übersetzer etwas verspricht.
                fahrzeug !== null && (
                  <span className={`merkmal merkmal--${CONVOY_LABELS[fahrzeug.convoyState].art}`}>
                    <ZustandsZeichen state={fahrzeug.convoyState} />
                    {CONVOY_LABELS[fahrzeug.convoyState].text}
                  </span>
                )
              )}
              {/*
                Der Rückstand — die eigentliche Zahl dieser Liste.
                
                Er stand hier klein und gedämpft unter dem Zustand und ging
                dabei unter. Dabei ist er das, wonach die Führung sucht, und
                die Liste ist nach ihm sortiert. Also groß, mit fester
                Zeichenbreite, damit die Zahlen untereinander stehen — und mit
                dem Wort darunter: „48 s" allein sagt nicht, wovon die Rede ist.
              */}
              {teilnehmer.visible && fahrzeug !== null && teilnehmer.role !== 'leader' && (
                <>
                  <div className="eintrag__rueckstand">
                    {formatDelay(fahrzeug.delaySeconds)}
                  </div>
                  <div className="eintrag__rueckstand-wort">Rückstand</div>
                </>
              )}
              {/*
                Der Pfeil klappt auf, was selten gebraucht wird.

                Er sitzt unten rechts im Eintrag und ist kleiner als die
                übrigen Flächen — bewusst. Die 24-mm-Regel gilt dem, was
                **während der Fahrt** gebraucht wird; die Führung übergibt man
                ein- oder zweimal an einem Tag, und meist im Stand. Was er
                aufklappt, hat wieder die volle Größe.

                Die Beschriftung nennt kein Rollenwort mehr: Was darunter
                steht, hängt davon ab, wer hinsieht.
              */}
              {gibtEtwasAufzuklappen && (
                <button
                  type="button"
                  className="knopf knopf--leise knopf--klapp"
                  aria-expanded={offen}
                  aria-label={
                    offen
                      ? `Mehr zu ${teilnehmer.displayName} schließen`
                      : `Mehr zu ${teilnehmer.displayName}`
                  }
                  onClick={() => {
                    setOffenFür((bisher) => (bisher === teilnehmer.id ? null : teilnehmer.id));
                  }}
                >
                  <ZeichenAufklappen offen={offen} />
                </button>
              )}
            </div>

            {/* Ausgeklappt: über die ganze Breite, unter allem anderen. */}
            {gibtEtwasAufzuklappen && offen && (
              <div className="eintrag__aktionen">
                {/*
                  Was der Zustand oben bedeutet — als ganzer Satz.

                  Bis zum 01.09.2026 gab es diese Erklärung nur im Warnbalken,
                  und den gibt es allein für die vier Zustände, die eine
                  Warnung auslösen. Für „Zurückgefallen" stand damit nirgends,
                  wodurch es sich von „Kein Anschluss" unterscheidet — obwohl
                  genau das die Frage ist, die sich die Führung stellt. Die
                  Sätze dafür waren geschrieben und ausgerollt und ließen sich
                  nicht lesen; drei der sieben hatten überhaupt keine
                  Ausgabestelle.

                  Hier und nicht in der Zeile darüber: Der Eintrag hält seinen
                  Zustand in **einer** Zeile (A-52), und ein Satz je Fahrzeug
                  kostete in jeder Kolonne Höhe. Aufgeklappt ist immer nur
                  einer, und wer aufklappt, hat gefragt.

                  Ein unsichtbares Fahrzeug hat keinen Zustand, den man
                  erklären könnte — dort steht „Unsichtbar" statt einer Pille,
                  und das erklärt sich selbst.
                */}
                {zustandssatz !== null && (
                  <p className="eintrag__erklaerung">
                    <strong>{zustandssatz.text}</strong> — {zustandssatz.erklaerung}
                  </p>
                )}

                {/* Beide mit Rückfrage — wer entfernt wurde, muss neu
                    beitreten, und die Führung abzugeben ist nichts, was aus
                    Versehen geschehen darf. */}
                {führungsflächen && (
                  <>
                    <button
                      type="button"
                      className="knopf knopf--leise"
                      onClick={() => {
                        if (confirm(`${teilnehmer.displayName} soll die Führung übernehmen?`)) {
                          onFührungÜbergeben(teilnehmer.id);
                        }
                      }}
                    >
                      Führung übergeben
                    </button>

                    {/* Für den Fall, dass jemand nicht mehr mitfährt, sich
                        aber nicht abgemeldet hat. */}
                    <button
                      type="button"
                      className="knopf knopf--leise"
                      onClick={() => {
                        if (
                          confirm(
                            `${teilnehmer.displayName} aus der Kolonne nehmen? ` +
                              'Zum Mitfahren müsste das Fahrzeug der Ausfahrt erneut beitreten.',
                          )
                        ) {
                          onEntfernen(teilnehmer.id);
                        }
                      }}
                    >
                      Aus der Kolonne nehmen
                    </button>
                  </>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
